import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useInView,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import ToolFunnelHeader from '@/components/tools/ToolFunnelHeader';
import { AGENT_LINK_ANCHORS, AgentLandingLink } from '@/components/growth-agent/AgentLandingLink';
import { useTrackOnce } from '@/lib/userTracking';
import Footer from '@/components/Footer';
import { EmbedInquiryModal } from '@/components/lead-gen-website/EmbedInquiryModal';
import { openEmbedInquiry } from '@/components/lead-gen-website/openEmbedInquiry';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Mail,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Coffee,
  Inbox,
  FileText,
  CheckCircle2,
  Zap,
  Star,
  Award,
  Clock,
  Globe,
  Plus,
  Minus,
  PlayCircle,
  Code2,
  PaintBucket,
  Lock,
  Image as ImageIcon,
  Pause,
  Play,
  SkipForward,
  SkipBack,
  RotateCcw,
  MapPin,
  Lightbulb,
  MessageSquare,
  PenLine,
  Calendar,
  Linkedin,
  Instagram,
  Utensils,
  Gauge,
  Bot,
} from 'lucide-react';
import { ORGANIC, useReducedMotion, TiltCard, MagneticButton, CountUp } from './LeadGenToolsLanding.shared';
import JoinedCarousel from '@/components/growth-agent/proof/JoinedCarousel';

// The below-the-fold section run (Live Demo, ROI calculator,
// testimonials, numbers, pricing/cross-sell band, FAQ, blog and final
// CTA) is split into its own React.lazy chunk so it does not weigh
// down the initial above-the-fold bundle. It still renders on mount
// (inside <Suspense>), just from a separate chunk fetched in parallel,
// and is idle-prefetched from the page component below.
const LeadGenToolsBelowFold = lazy(() => import('./LeadGenToolsLanding.belowFold'));

// ─── Helpers ───────────────────────────────────────────────────────

// Three-depth cursor parallax. Writes CSS vars that elements consume in
// translate3d transforms. Avoids per-frame React re-renders entirely.
function useMouseParallax(disabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let tx = 0,
      ty = 0,
      l1x = 0,
      l1y = 0,
      l2x = 0,
      l2y = 0,
      l3x = 0,
      l3y = 0;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    const tick = () => {
      l1x += (tx - l1x) * 0.12;
      l1y += (ty - l1y) * 0.12;
      l2x += (tx - l2x) * 0.07;
      l2y += (ty - l2y) * 0.07;
      l3x += (tx - l3x) * 0.04;
      l3y += (ty - l3y) * 0.04;
      el.style.setProperty('--p1x', String(l1x));
      el.style.setProperty('--p1y', String(l1y));
      el.style.setProperty('--p2x', String(l2x));
      el.style.setProperty('--p2y', String(l2y));
      el.style.setProperty('--p3x', String(l3x));
      el.style.setProperty('--p3y', String(l3y));
      raf = requestAnimationFrame(tick);
    };
    el.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      el.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [disabled]);
  return ref;
}





// Sticky bottom CTA bar that appears once user scrolls past the hero.
// Marketing 101: when the visitor is convinced but the primary CTA is
// off-screen, give them a one-tap shortcut.
function StickyCTABar({ onClick }: { onClick: () => void }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => {
      const past = window.scrollY > window.innerHeight * 0.9;
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 800;
      setShow(past && !nearBottom);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: ORGANIC }}
          className="fixed bottom-4 right-4 z-40 hidden md:block"
        >
          <button
            onClick={onClick}
            className="group relative px-5 h-12 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold text-sm shadow-2xl shadow-fuchsia-500/40 transition-all overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <span className="relative flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Get my embed code
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────
// LeadMagnet — abstract "gravity core" mark. A glowing inner sphere
// with two slowly-counter-rotating orbital rings, plus 4 small lead
// particles travelling along the rings. As the cursor approaches the
// icon area, the orbits contract and the particles spiral inward
// toward the core, the rings spin faster, and the core flares.
// Conveys "magnetic pull" without being a cartoon horseshoe magnet.
// ─────────────────────────────────────────────────────────────────
// Creative backdrop behind the "Lead Magnet" headline. Mock leads
// (small chips with name + email) drift in from outer edges, get
// pulled toward the centre as if magneted, then fade as they're
// "captured". Loops infinite. Sits on a layer below the heading
// text so the chips visibly pass behind the letters.
function LeadStreamBackdrop() {
  const reduced = useReducedMotion();
  if (reduced) return null;

  // Mock leads — diverse names + plausible emails.
  const leads = [
    { name: 'Priya', email: 'p@hotelannapurna.com', dot: '#a855f7' },
    { name: 'Rohan', email: 'rohan@maurya.co', dot: '#22d3ee' },
    { name: 'Meera', email: 'meera@coffeeloft.in', dot: '#f472b6' },
    { name: 'Arjun', email: 'a@stayeazy.in', dot: '#fbbf24' },
    { name: 'Kavya', email: 'k@spicegarden.in', dot: '#34d399' },
    { name: 'Vikram', email: 'v@bluelagoon.in', dot: '#818cf8' },
    { name: 'Aisha', email: 'aisha@tajdine.in', dot: '#fb7185' },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible">
      {leads.map((l, i) => {
        // Each chip starts off to the left/right at a random vertical
        // offset, drifts toward the centre (where "Magnet" sits),
        // shrinks + fades as it's "absorbed".
        const fromLeft = i % 2 === 0;
        const startX = fromLeft ? -180 - i * 30 : 220 + i * 30;
        const verticalBand = (i - 3) * 26; // spread across the heading vertical
        return (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 px-2 py-1 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-sm flex items-center gap-1.5 whitespace-nowrap"
            style={{ transform: 'translate(-50%, -50%)' }}
            initial={{ x: startX, y: verticalBand, opacity: 0, scale: 0.8 }}
            animate={{
              x: [startX, startX * 0.5, 30, 70],
              y: [verticalBand, verticalBand * 0.5, 0, -8],
              opacity: [0, 0.55, 0.7, 0],
              scale: [0.8, 1, 0.9, 0.55],
            }}
            transition={{
              duration: 5.5 + i * 0.4,
              delay: i * 0.7,
              repeat: Infinity,
              ease: ORGANIC,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: l.dot, boxShadow: `0 0 6px ${l.dot}` }}
            />
            <span className="text-[10px] font-medium text-white/70">{l.name}</span>
            <span className="text-[10px] text-white/30">·</span>
            <span className="text-[10px] text-white/40 font-mono">{l.email}</span>
          </motion.div>
        );
      })}

      {/* Magnet pulse — soft radial glow at centre that "draws" the
          chips in. Gives the backdrop directional intent. */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(168,85,247,0.25) 0%, rgba(236,72,153,0.12) 35%, transparent 70%)',
        }}
        animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

function LeadMagnet() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  // Cursor proximity → spring into 0..1 "pull" intensity.
  const pull = useMotionValue(0);
  const pullS = useSpring(pull, { stiffness: 90, damping: 18 });
  // Subtle 3D tilt toward cursor for the whole composition.
  const tx = useMotionValue(0);
  const ty = useMotionValue(0);
  const sx = useSpring(tx, { stiffness: 90, damping: 18 });
  const sy = useSpring(ty, { stiffness: 90, damping: 18 });
  const rotX = useTransform(sy, (v) => `${(-v * 8).toFixed(2)}deg`);
  const rotY = useTransform(sx, (v) => `${(v * 10).toFixed(2)}deg`);

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const cxv = rect.left + rect.width / 2;
      const cyv = rect.top + rect.height / 2;
      const dist = Math.hypot(e.clientX - cxv, e.clientY - cyv);
      // Pull engages within 220px, peaks at 60px and closer.
      pull.set(Math.max(0, Math.min(1, 1 - (dist - 60) / 160)));
      tx.set(Math.max(-1, Math.min(1, (e.clientX - cxv) / 80)));
      ty.set(Math.max(-1, Math.min(1, (e.clientY - cyv) / 80)));
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [pull, tx, ty, reduced]);

  // 4 particles distributed across two rings. Each has an idle
  // orbit radius (outer or inner ring) and gets pulled to ~0 on hover.
  const particles = [
    { color: '#ec4899', baseAngle: 30, ring: 'outer' as const, speed: 18 },
    { color: '#a855f7', baseAngle: 150, ring: 'outer' as const, speed: 22 },
    { color: '#f472b6', baseAngle: 80, ring: 'inner' as const, speed: 12 },
    { color: '#c084fc', baseAngle: 230, ring: 'inner' as const, speed: 14 },
  ];

  return (
    <motion.div
      ref={ref}
      className="relative inline-block align-middle mr-3 lg:mr-4 shrink-0"
      style={{
        width: 'clamp(54px, 7vw, 88px)',
        height: 'clamp(54px, 7vw, 88px)',
        rotateX: rotX,
        rotateY: rotY,
        transformPerspective: 700,
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Outer glow halo — intensifies on cursor approach */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(236,72,153,0.45), rgba(168,85,247,0.20) 45%, transparent 70%)',
          filter: 'blur(14px)',
          scale: useTransform(pullS, [0, 1], [1, 1.35]),
          opacity: useTransform(pullS, [0, 1], [0.55, 0.95]),
        }}
      />

      {/* Outer orbital ring — rotates slowly, dashes give "energy" feel */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          animation: reduced ? undefined : 'orbitSpin 22s linear infinite',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="rgba(236,72,153,0.45)"
            strokeWidth="1"
            strokeDasharray="3 4"
          />
        </svg>
      </motion.div>

      {/* Inner orbital ring — counter-rotates faster, brighter */}
      <motion.div
        className="absolute inset-[14%] pointer-events-none"
        style={{
          animation: reduced ? undefined : 'orbitSpinReverse 14s linear infinite',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="rgba(168,85,247,0.55)"
            strokeWidth="1.2"
            strokeDasharray="2 3"
          />
        </svg>
      </motion.div>

      {/* Lead particles — orbit on their ring at idle, spiral inward
          toward the core as `pull` rises. We compute idle position as
          a CSS animation (orbital travel) and the inward-pull as a
          radius scaler so the two compose cleanly. */}
      {particles.map((p, i) => {
        const baseR = p.ring === 'outer' ? 44 : 30;
        const radiusVar = useTransform(pullS, [0, 1], [baseR, 4]);
        return (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 pointer-events-none"
            style={{
              animation: reduced
                ? undefined
                : `particleOrbit-${i} ${p.speed}s linear infinite`,
            }}
          >
            <motion.div
              style={{
                width: 'clamp(5px, 0.85vw, 8px)',
                height: 'clamp(5px, 0.85vw, 8px)',
                background: `radial-gradient(circle at 30% 30%, white, ${p.color} 75%)`,
                boxShadow: `0 0 10px ${p.color}b0, 0 0 20px ${p.color}50`,
                borderRadius: '50%',
                position: 'absolute',
                top: '0',
                left: '0',
                translateX: useTransform(radiusVar, (r) => `${Math.cos((p.baseAngle * Math.PI) / 180) * r}%`),
                translateY: useTransform(radiusVar, (r) => `${Math.sin((p.baseAngle * Math.PI) / 180) * r}%`),
              }}
            />
          </motion.div>
        );
      })}

      {/* Core orb — gradient sphere with highlight + inner shimmer */}
      <motion.div
        className="absolute inset-[28%] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 32% 28%, #ffffff 0%, #f0abfc 18%, #ec4899 55%, #a855f7 100%)',
          boxShadow:
            '0 0 16px rgba(236,72,153,0.6), inset 0 -3px 10px rgba(167,40,116,0.5), inset 0 3px 8px rgba(255,255,255,0.3)',
          scale: useTransform(pullS, [0, 1], [1, 1.18]),
          filter: useTransform(pullS, [0, 1], ['brightness(1)', 'brightness(1.25)']),
        }}
      />

      {/* Shockwave ring — emerges when cursor is close */}
      <motion.div
        className="absolute inset-0 rounded-full border pointer-events-none"
        style={{
          borderColor: 'rgba(236,72,153,0.5)',
          scale: useTransform(pullS, [0, 1], [0.9, 1.5]),
          opacity: useTransform(pullS, [0, 1], [0, 0.5]),
        }}
      />

      <style>{`
        @keyframes orbitSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes orbitSpinReverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes particleOrbit-0 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes particleOrbit-1 { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
        @keyframes particleOrbit-2 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes particleOrbit-3 { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FloatingLeadCount — small "Leads this week" stat pinned to the
// top corner of the hero visual area. Slow tick-up so it feels live
// without being noisy.
// ─────────────────────────────────────────────────────────────────
function FloatingLeadCount() {
  const [n, setN] = useState(1247);
  useEffect(() => {
    const id = setInterval(() => setN((v) => v + Math.floor(Math.random() * 3) + 1), 5000);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 0.6, ease: ORGANIC }}
      className="absolute top-0 lg:-top-2 left-2 lg:left-0 z-20 px-3.5 py-2 rounded-lg bg-white/[0.04] backdrop-blur border border-white/10 flex items-center gap-2 shadow-lg"
    >
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
        Leads this week
      </div>
      <motion.div
        key={n}
        initial={{ scale: 1.15, color: '#a78bfa' }}
        animate={{ scale: 1, color: '#ffffff' }}
        transition={{ duration: 0.5 }}
        className="text-base font-bold tabular-nums"
      >
        {n.toLocaleString()}
      </motion.div>
      <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// TwoWays — the page previously sold only the embed: put a tool on
// your site, collect leads. That is half the product. Agencies also
// run audits on prospects they are already chasing and send the
// report to open the conversation, and nothing on the page said so.
// Naming both jobs plainly lets a visitor recognise their own before
// they reach the feature list.
// ─────────────────────────────────────────────────────────────────
function TwoWays() {
  return (
    <section className="relative bg-gradient-to-b from-gray-950 to-[#0d0d14] py-24 lg:py-28 border-t border-white/5">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white lg:text-4xl">
            Wait for leads, or go and get them.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-gray-400">
            Most agencies end up doing both. The same audits power each one, so whichever you start with, the other is already paid for.
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-2">
          {/* Inbound */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-8">
            <div className="text-xs font-medium uppercase tracking-widest text-cyan-300/80">On your website</div>
            <h3 className="mt-3 text-xl font-semibold text-white">Let visitors audit themselves</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">
              Put a free audit on your site. A visitor runs it on their own business, leaves their details to get the report, and lands in your inbox already interested. No ad spend, and the first conversation starts warm.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-gray-300">
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />One snippet, live in about two minutes</li>
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />Every report carries your brand, not ours</li>
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />Leads only ever reach your inbox</li>
            </ul>
          </div>

          {/* Outbound */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-8">
            <div className="text-xs font-medium uppercase tracking-widest text-violet-300/80">Before the call</div>
            <h3 className="mt-3 text-xl font-semibold text-white">Audit a prospect you are already chasing</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">
              Run the same audits on any business, without touching their accounts. You walk into the call already knowing what is broken, and the report does the explaining for you.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-gray-300">
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-violet-400" />Works on any business, no access needed</li>
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-violet-400" />Send it as a client-ready report under your brand</li>
              <li className="flex gap-2.5"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-violet-400" />Audit competitors too, before a pitch</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Which cut of the film to fetch, and whether to start it at all.
 *
 * The hero is about 640px wide on a laptop and 390px on a phone, so a phone
 * gains nothing from the 1080p master and pays for it in data and in decode
 * work on a weak chip. A visitor on a metered or slow connection should not
 * have a film pushed at them at all: they get the poster, and the film only
 * loads if they ask for it by tapping.
 */
function chooseFilm(): { src: string; autoplay: boolean } {
  if (typeof window === 'undefined') return { src: '/fly-launch-video.mp4', autoplay: true };
  const small = window.matchMedia('(max-width: 900px)').matches;
  const src = small ? '/fly-launch-video-720.mp4' : '/fly-launch-video.mp4';
  const link = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  const frugal = link?.saveData === true || /(^|-)2g$/.test(link?.effectiveType ?? '');
  return { src, autoplay: !frugal };
}

// ─────────────────────────────────────────────────────────────────
// LaunchVideo — the launch film in the hero, played in full.
//
// It used to open on a short silent loop with a "See how it works"
// button that swapped in the real film. Visitors read the looping
// clip as the whole video, so the film almost never got watched.
// The film is now what plays, from the first frame.
//
// Sound: every browser blocks audio until a visitor has interacted
// with the page, so "start unmuted" is a request, not a decision we
// can make. We ask for sound, and when the browser refuses we fall
// back to a silent start and say so on the film, quietly. Anywhere
// on the film toggles sound, so getting audio is one click from
// wherever the pointer already is.
//
// poster paints the first frame instantly so the hero is never an
// empty black box while the file streams in.
// ─────────────────────────────────────────────────────────────────
function LaunchVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  // Decided once: re-deciding mid-playback would swap the file and restart it.
  const [film] = useState(chooseFilm);
  // True when we deliberately did not start it (metered or slow connection).
  const [waitingForTap, setWaitingForTap] = useState(!film.autoplay);
  // True once the browser has refused sound, so the film can say "click for
  // sound" instead of looking like it simply has none.
  const [soundBlocked, setSoundBlocked] = useState(false);
  // The film is a committed asset, but it went missing once before (never
  // committed, so production served the app's HTML for it and the hero fell
  // back to a five second loop that everyone mistook for the film). If it ever
  // fails to load again, the short silent loop keeps the hero alive rather
  // than leaving a still poster.
  const [filmFailed, setFilmFailed] = useState(false);

  // Ask for sound on load, and accept the answer.
  //
  // Chrome allows unmuted autoplay on sites a visitor uses often, Safari and a
  // first-time Chrome visitor do not. Both paths have to work: request sound,
  // and if play() is rejected, start silent instead so the hero is never a
  // still frame. The two attempts are deliberately sequential.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!film.autoplay) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cancelled = false;
    const startSilently = () => {
      if (cancelled || !videoRef.current) return;
      const v = videoRef.current;
      v.muted = true;
      setMuted(true);
      setSoundBlocked(true);
      void v.play().catch(() => undefined);
    };

    el.muted = false;
    el.volume = 1;
    void el
      .play()
      .then(() => {
        if (cancelled) return;
        // Some browsers honour play() and then mute it themselves rather than
        // rejecting, so the element is the source of truth, not the promise.
        if (el.muted) startSilently();
        else setMuted(false);
      })
      .catch(startSilently);

    return () => {
      cancelled = true;
    };
  }, [film.autoplay]);

  // Playing to an empty room helps nobody: pause once the hero scrolls away,
  // and pick it back up on the way past. Sound is left exactly as the visitor
  // set it.
  useEffect(() => {
    const el = videoRef.current;
    const frame = frameRef.current;
    if (!el || !frame) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!waitingForTap) void el.play().catch(() => undefined);
        } else {
          el.pause();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(frame);
    return () => io.disconnect();
  }, [waitingForTap]);

  /** Anywhere on the film turns sound on or off, and starts it if we held it back. */
  const onFilmClick = () => {
    const el = videoRef.current;
    if (!el) return;
    if (waitingForTap) {
      setWaitingForTap(false);
      el.muted = false;
      el.volume = 1;
      setMuted(false);
      setSoundBlocked(false);
      void el.play().catch(() => undefined);
      return;
    }
    const next = !el.muted;
    el.muted = next;
    el.volume = 1;
    setMuted(next);
    // The first click is also the interaction browsers were waiting for, so
    // there is nothing left to warn about.
    setSoundBlocked(false);
    if (el.paused) void el.play().catch(() => undefined);
  };

  return (
    <div className="relative">
      {/* Ambient bloom so the film sits in light, not on a flat panel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[125%] w-[112%] -translate-x-1/2 -translate-y-1/2 rounded-[3rem] bg-gradient-to-tr from-violet-600/25 via-fuchsia-500/15 to-cyan-400/25 blur-3xl"
      />
      <div
        ref={frameRef}
        onClick={onFilmClick}
        className="group relative aspect-video cursor-pointer overflow-hidden rounded-2xl bg-black shadow-[0_40px_90px_-25px_rgba(0,0,0,0.85)] ring-1 ring-white/15"
      >
        {/* preload="metadata" rather than "auto": the poster carries the first
            impression, and the film streams as it plays instead of being
            pulled down in full before anything appears. */}
        <video
          key={filmFailed ? 'loop' : 'film'}
          ref={videoRef}
          poster="/fly-launch-poster.jpg"
          playsInline
          loop
          preload={film.autoplay ? 'metadata' : 'none'}
          className="h-full w-full object-cover"
          onError={() => {
            if (filmFailed) return;
            console.warn('[LaunchVideo] The film could not be loaded, falling back to the silent loop');
            setFilmFailed(true);
          }}
        >
          {filmFailed ? (
            <>
              <source src="/fly-hero-loop.webm" type="video/webm" />
              <source src="/fly-hero-loop.mp4" type="video/mp4" />
            </>
          ) : (
            <source src={film.src} type="video/mp4" />
          )}
        </video>

        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10"
        />


        {/* Only while the browser is holding sound back, and only until the
            first click. A permanent label would just be clutter. */}
        {waitingForTap && !filmFailed && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <span className="rounded-full border border-white/20 bg-black/60 px-4 py-2 text-xs font-medium text-white/90 backdrop-blur-md">
              Tap to watch, with sound
            </span>
          </div>
        )}

        {soundBlocked && muted && !waitingForTap && !filmFailed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center">
            <span className="rounded-full border border-white/20 bg-black/60 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-md">
              Click for sound
            </span>
          </div>
        )}

        {/* Keeps the state visible, and keeps sound reachable by keyboard,
            which a click target on the film alone would not. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onFilmClick();
          }}
          aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
          className="absolute bottom-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          {muted ? (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 5 6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 5 6 9H2v6h4l5 4V5z" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7" />
              <path d="M19 5a9 9 0 0 1 0 14" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Agency logos.
//
// This page sells to agencies, so the strip shows agencies. Earlier versions
// used six invented names, then ZoomLocal's client set (mostly restaurants and
// clinics, true but the wrong audience). These seven are the agencies among
// that set.
//
// Each logo was converted to a trimmed, transparent PNG from its original
// jpeg/webp, with the background knocked out and the art cropped to its own
// bounds so every logo optically fills the same height.
//
// They sit on white tiles because the source logos were drawn for light
// backgrounds and this page is near black: Digital Tokri, SySpree and The
// Scribblers are effectively invisible on the dark ground. Two of the seven
// (24 Arc Media, Brainmines) were drawn light-on-dark, so their art is
// luminance-inverted to read on the tile while keeping its hue.
// ─────────────────────────────────────────────────────────────────
const AGENCY_LOGOS = [
  { name: '24 Arc Media',  src: '/lovable-uploads/agency-24-arc-media.png' },
  { name: 'Brainmines',    src: '/lovable-uploads/agency-brainmines.png' },
  { name: 'Digital Tokri', src: '/lovable-uploads/agency-digital-tokri.png' },
  { name: 'LeadstoSell',   src: '/lovable-uploads/agency-leadstosell.png' },
  { name: 'Digitize Matrix', src: '/lovable-uploads/agency-digitiz-matrix.png' },
  { name: 'SySpree',       src: '/lovable-uploads/agency-syspree.png' },
  { name: 'The Scribblers', src: '/lovable-uploads/agency-the-scribblers.png' },
  // Kept as its own blue square rather than background-removed: the blue IS
  // the mark, and knocking it out leaves white artwork that vanishes on a
  // light strip.
  { name: 'ZoomLocal',      src: '/lovable-uploads/agency-zoomlocal.png' },
];

// Continuous marquee. The track holds the list twice and translates exactly
// -50%, so the second copy lands where the first began and the loop is
// seamless rather than snapping back.
//
// Logos render at full colour and full opacity, never greyed or
// dimmed-until-hover. That treatment reads as decoration, and these are the
// proof, so they stay lit.
//
// Animation stops under prefers-reduced-motion: an endless horizontal crawl is
// a common vestibular trigger, and a static strip still shows every logo.
function TrustStrip() {
  return (
    // The whole band is light, not just the logo tiles.
    //
    // The page runs near black, and most of these logos are dark artwork drawn
    // for white backgrounds, so on the dark ground they camouflaged into it.
    // Putting each on its own white chip fixed legibility but left a row of
    // floating cards. A single light band reads as a deliberate section, gives
    // every logo the background it was designed for, and needs no per-logo
    // treatment.
    <section className="bg-violet-50 py-8">
      <p className="text-center text-[13px] font-bold uppercase tracking-[0.18em] text-violet-950 mb-6">
        Agencies growing with Fly
      </p>
      <div
        className="relative overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 7%, black 93%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 7%, black 93%, transparent)',
        }}
      >
        <div className="flex w-max items-stretch gap-10 animate-[fly-logo-marquee_40s_linear_infinite] motion-reduce:animate-none">
          {[...AGENCY_LOGOS, ...AGENCY_LOGOS].map((logo, i) => (
            <figure
              key={`${logo.src}-${i}`}
              className="flex w-[132px] shrink-0 flex-col items-center justify-end gap-2"
            >
              <span className="flex h-[46px] items-center justify-center">
                <img
                  src={logo.src}
                  alt={logo.name}
                  loading="lazy"
                  decoding="async"
                  className="max-h-[46px] w-auto max-w-full object-contain"
                />
              </span>
              <figcaption className="text-center text-[11px] font-medium text-violet-950/75">
                {logo.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <style>{`@keyframes fly-logo-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </section>
  );
}

// HeroDemoStage — the entire auto-playing demo. Owns the state
// machine, renders the embed card, an overlay cursor that points
// at what's "clicking", and a smartphone mockup below where the
// lead lands. All three sub-components consume the same shared
// phase so the cursor, the card UI, and the phone stay perfectly
// in sync. Cycles every ~13 seconds.
// ─────────────────────────────────────────────────────────────────
type HeroPhase = 'rebranding' | 'typing' | 'searching' | 'analyzing' | 'result' | 'emailing' | 'success';

// Each cycle the entire embed re-skins to a different agency — same
// product, different brand. The visitor sees the white-label promise
// kept in front of their eyes, not just claimed in copy. Colors,
// logo gradient, agency name, business + email sample all swap.
interface DemoBrand {
  agency: string;
  domain: string;
  from: string; // primary color (hex)
  to: string; // secondary color (hex)
  initial: string;
  business: string;
  city: string;
  query: string;
  email: string;
  phone: string; // Mock non-routable number — repeating digits so
  // it's clearly a placeholder, not a real visitor
  // GBP profile fields — rendered as a Google-style business card
  // on the audit result screen
  bizCategory: string;
  bizAddress: string;
  bizHours: string;
  bizPhone: string;
  bizWebsite: string;
  bizRating: number;
  bizReviews: number;
}
const DEMO_BRANDS: DemoBrand[] = [
  {
    agency: 'ZoomLocal',
    domain: 'zoomlocal.in',
    from: '#a855f7',
    to: '#ec4899',
    initial: 'A',
    business: 'Hotel Annapurna',
    city: 'Patna',
    query: 'Hotel Annapurna',
    email: 'priya@hotelannapurna.com',
    phone: '+91 99999 12345',
    bizCategory: '3-star hotel',
    bizAddress: 'Fraser Rd, Patna 800001',
    bizHours: 'Open 24 hours',
    bizPhone: '+91 612 220 4567',
    bizWebsite: 'hotelannapurna.com',
    bizRating: 4.6,
    bizReviews: 248,
  },
  {
    agency: 'Digital Tokri',
    domain: 'lumio.in',
    from: '#3b82f6',
    to: '#06b6d4',
    initial: 'L',
    business: 'The Coffee Loft',
    city: 'Bangalore',
    query: 'The Coffee Loft',
    email: 'meera@coffeeloft.in',
    phone: '+91 88888 67890',
    bizCategory: 'Coffee shop',
    bizAddress: '12 MG Road, Bangalore 560001',
    bizHours: 'Open · Closes 11 PM',
    bizPhone: '+91 80 4123 4567',
    bizWebsite: 'thecoffeeloft.in',
    bizRating: 4.8,
    bizReviews: 412,
  },
  {
    agency: 'SySpree',
    domain: 'northstarlabs.io',
    from: '#10b981',
    to: '#84cc16',
    initial: 'N',
    business: 'Maurya Kitchen',
    city: 'Mumbai',
    query: 'Maurya Kitchen',
    email: 'rohan@mauryakitchen.com',
    phone: '+91 77777 23456',
    bizCategory: 'North Indian restaurant',
    bizAddress: '7 Linking Rd, Bandra W, Mumbai 400050',
    bizHours: 'Open · Closes 10:30 PM',
    bizPhone: '+91 22 2640 8910',
    bizWebsite: 'mauryakitchen.in',
    bizRating: 4.4,
    bizReviews: 1203,
  },
];

// Mock brand logos — small SVG marks for each agency that look like
// real wordmarks rather than just initials in a square. Each brand
// gets its own icon style + wordmark treatment so the white-label
// re-skin in the demo lands more clearly.
function BrandLogo({
  brand,
  brandIdx,
  size = 'sm',
  showWordmark = true,
}: {
  brand: DemoBrand;
  brandIdx: number;
  size?: 'xs' | 'sm' | 'md';
  showWordmark?: boolean;
}) {
  const px = size === 'md' ? 26 : size === 'sm' ? 20 : 16;
  const wordSize = size === 'md' ? 'text-[14px]' : size === 'sm' ? 'text-[12px]' : 'text-[10px]';
  const subSize = size === 'md' ? 'text-[7.5px]' : 'text-[6.5px]';
  const idBase = `bl-${brandIdx}`;

  let icon: React.ReactNode = null;
  let wordmark: React.ReactNode = null;

  if (brand.agency === 'ZoomLocal') {
    // 4 sound-wave bars that "pulse" as a small lifelike touch
    icon = (
      <svg width={px} height={px} viewBox="0 0 24 24">
        <defs>
          <linearGradient id={`${idBase}-amp`} x1="0" y1="0" x2="24" y2="24">
            <stop offset="0%" stopColor={brand.from} />
            <stop offset="100%" stopColor={brand.to} />
          </linearGradient>
        </defs>
        <rect x="2" y="9" width="3.2" height="6" rx="1.2" fill={`url(#${idBase}-amp)`} />
        <rect x="7" y="6" width="3.2" height="12" rx="1.2" fill={`url(#${idBase}-amp)`} />
        <rect x="12" y="3" width="3.2" height="18" rx="1.2" fill={`url(#${idBase}-amp)`} />
        <rect x="17" y="8" width="3.2" height="8" rx="1.2" fill={`url(#${idBase}-amp)`} />
      </svg>
    );
    wordmark = (
      <div className="flex flex-col leading-none">
        <span className={`${wordSize} font-extrabold text-white tracking-tight`}>Amplify</span>
        {size !== 'xs' && (
          <span
            className={`${subSize} font-semibold uppercase tracking-[0.2em] mt-0.5`}
            style={{ color: brand.from }}
          >
            Digital
          </span>
        )}
      </div>
    );
  } else if (brand.agency === 'Digital Tokri') {
    // Lowercase "l" with a glowing spark, evoking light/illumination
    icon = (
      <svg width={px} height={px} viewBox="0 0 24 24">
        <defs>
          <linearGradient id={`${idBase}-lum`} x1="0" y1="0" x2="24" y2="24">
            <stop offset="0%" stopColor={brand.from} />
            <stop offset="100%" stopColor={brand.to} />
          </linearGradient>
          <radialGradient id={`${idBase}-lum-glow`}>
            <stop offset="0%" stopColor={brand.from} stopOpacity="0.7" />
            <stop offset="100%" stopColor={brand.from} stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* circular base */}
        <circle cx="12" cy="14" r="9" fill={`url(#${idBase}-lum)`} opacity="0.16" />
        {/* "L" */}
        <path
          d="M 9 5 L 9 17 L 17 17"
          stroke={`url(#${idBase}-lum)`}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* glow spark */}
        <circle cx="9" cy="5" r="3" fill={`url(#${idBase}-lum-glow)`} />
        <circle cx="9" cy="5" r="1.4" fill="#fff" />
      </svg>
    );
    wordmark = (
      <span
        className={`${wordSize} font-light tracking-tight text-white lowercase`}
        style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}
      >
        lumio<span style={{ color: brand.from }}>.</span>
      </span>
    );
  } else {
    // SySpree — 4-point compass star with center dot
    icon = (
      <svg width={px} height={px} viewBox="0 0 24 24">
        <defs>
          <linearGradient id={`${idBase}-ns`} x1="0" y1="0" x2="24" y2="24">
            <stop offset="0%" stopColor={brand.from} />
            <stop offset="100%" stopColor={brand.to} />
          </linearGradient>
        </defs>
        <path
          d="M 12 1.5 L 13.5 10.5 L 22.5 12 L 13.5 13.5 L 12 22.5 L 10.5 13.5 L 1.5 12 L 10.5 10.5 Z"
          fill={`url(#${idBase}-ns)`}
        />
        <circle cx="12" cy="12" r="1.6" fill="#fff" />
      </svg>
    );
    wordmark = (
      <div className="flex flex-col leading-none">
        <span
          className={`${wordSize} font-bold text-white tracking-tight`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          NorthStar
        </span>
        {size !== 'xs' && (
          <span
            className={`${subSize} font-semibold uppercase tracking-[0.28em] mt-0.5`}
            style={{ color: brand.from }}
          >
            Labs
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {icon}
      {showWordmark && wordmark}
    </div>
  );
}

// Controls bar that sits directly under the embed card. Pause /
// resume the auto-cycle, step manually to the next phase, or
// restart from the top. Also doubles as the home for the live
// "leads this week" counter so it's not floating in dead space.
function DemoControls({
  paused,
  onTogglePause,
  onNext,
  onPrev,
}: {
  paused: boolean;
  onTogglePause: () => void;
  onNext: () => void;
  onPrev: () => void;
}) {
  // Round floating-button design — each glyph sits in a soft circle
  // that brightens on hover. Center play/pause is bigger and more
  // visually weighted, making it the clear primary action. Side
  // buttons are smaller & subtler. Reads cleanly at small size and
  // matches modern media-player conventions (Spotify / Apple Music
  // / YouTube transport bars).
  const PrevGlyph = () => (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
      <path d="M6 5h2v14H6zM10 12l8-7v14z" />
    </svg>
  );
  const NextGlyph = () => (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
      <path d="M16 5h2v14h-2zM6 5l8 7-8 7z" />
    </svg>
  );
  const PlayGlyph = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current ml-0.5">
      <path d="M7 5l12 7-12 7z" />
    </svg>
  );
  const PauseGlyph = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );

  return (
    <div className="mt-4 flex items-center justify-center gap-3">
      <button
        onClick={onPrev}
        className="group w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.10] text-white/60 hover:text-white transition-all"
        aria-label="Previous step"
      >
        <PrevGlyph />
      </button>
      {/* Center play/pause — larger, brand-tinted gradient ring,
          stronger visual presence as the primary control */}
      <button
        onClick={onTogglePause}
        className="group relative w-11 h-11 rounded-full flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30 hover:shadow-xl hover:shadow-fuchsia-500/40 hover:scale-105 active:scale-95 transition-all"
        aria-label={paused ? 'Play demo' : 'Pause demo'}
      >
        <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/10 transition-colors" />
        <span className="relative">{paused ? <PlayGlyph /> : <PauseGlyph />}</span>
      </button>
      <button
        onClick={onNext}
        className="group w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.10] text-white/60 hover:text-white transition-all"
        aria-label="Next step"
      >
        <NextGlyph />
      </button>
    </div>
  );
}

function useHeroDemoMachine() {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<HeroPhase>('rebranding');
  const [brandIdx, setBrandIdx] = useState(0);
  const [typedQ, setTypedQ] = useState('');
  const [typedE, setTypedE] = useState('');
  const [typedP, setTypedP] = useState('');
  const [analyzeStep, setAnalyzeStep] = useState(0);
  // Sub-step within a phase — drives the cursor target so it can hit
  // multiple elements during a single phase (e.g. suggestion → Run
  // button during 'searching') without forcing extra phase enums.
  const [cursorStep, setCursorStep] = useState(0);
  // User controls — viewers can pause auto-advance or manually
  // step through phases to take their time on each screen.
  const [paused, setPaused] = useState(false);
  const brand = DEMO_BRANDS[brandIdx];

  useEffect(() => {
    if (reduced) {
      setPhase('result');
      return;
    }
    if (paused) return;
    setCursorStep(0);
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    // Timing rule: pauses sit at page-level (screen transitions),
    // not after every keystroke. Within an action like typing, we
    // tick quickly + move on. The "reading pause" only applies on
    // screens where there's something to absorb (rebrand reveal,
    // result/score, success/lead-arrival).
    if (phase === 'rebranding') {
      // Page-level pause: viewer reads the white-label re-skin.
      timeouts.push(setTimeout(() => setPhase('typing'), 2400));
    } else if (phase === 'typing') {
      // Brisk typing + small handoff (no long "read the field" wait).
      let i = 0;
      const tick = () => {
        i++;
        setTypedQ(brand.query.slice(0, i));
        if (i < brand.query.length) timeouts.push(setTimeout(tick, 110));
        else timeouts.push(setTimeout(() => setPhase('searching'), 500));
      };
      timeouts.push(setTimeout(tick, 600));
    } else if (phase === 'searching') {
      // Hover suggestion → click Run. Quick + decisive.
      timeouts.push(setTimeout(() => setCursorStep(1), 900));
      timeouts.push(setTimeout(() => setPhase('analyzing'), 1700));
    } else if (phase === 'analyzing') {
      // Spinner does exactly 1.5 rotations (1.6s), then transitions
      // straight into the report. No infinite spin.
      timeouts.push(setTimeout(() => setAnalyzeStep(1), 550));
      timeouts.push(setTimeout(() => setAnalyzeStep(2), 1100));
      timeouts.push(setTimeout(() => setPhase('result'), 1700));
    } else if (phase === 'result') {
      // Page-level reading pause — cursor pans the report so the
      // viewer follows the score → bars → quick-wins.
      timeouts.push(setTimeout(() => setCursorStep(1), 1400));
      timeouts.push(setTimeout(() => setCursorStep(2), 2800));
      timeouts.push(setTimeout(() => setPhase('emailing'), 4200));
    } else if (phase === 'emailing') {
      // 3-step lead-capture flow:
      //   cursorStep 0: type email
      //   cursorStep 1: cursor moves to phone field, types phone
      //   cursorStep 2: cursor on Get Free Report button → click
      //                 ripple → handoff to success
      let i = 0;
      const typeEmail = () => {
        i++;
        setTypedE(brand.email.slice(0, i));
        if (i < brand.email.length) timeouts.push(setTimeout(typeEmail, 70));
        else {
          // Email done — move cursor to phone field, start typing phone
          timeouts.push(
            setTimeout(() => {
              setCursorStep(1);
              let j = 0;
              const typePhone = () => {
                j++;
                setTypedP(brand.phone.slice(0, j));
                if (j < brand.phone.length) timeouts.push(setTimeout(typePhone, 55));
                else {
                  // Phone done — cursor on button, then submit
                  timeouts.push(setTimeout(() => setCursorStep(2), 450));
                  timeouts.push(setTimeout(() => setPhase('success'), 1100));
                }
              };
              timeouts.push(setTimeout(typePhone, 250));
            }, 350)
          );
        }
      };
      timeouts.push(setTimeout(typeEmail, 600));
    } else if (phase === 'success') {
      // Page-level pause — the "lead arrives on phone" payoff.
      // Long enough to read the notification + caption.
      timeouts.push(setTimeout(() => setCursorStep(1), 1400));
      timeouts.push(
        setTimeout(() => {
          setTypedQ('');
          setTypedE('');
          setTypedP('');
          setAnalyzeStep(0);
          setBrandIdx((i) => (i + 1) % DEMO_BRANDS.length);
          setPhase('rebranding');
        }, 5000)
      );
    }
    return () => timeouts.forEach(clearTimeout);
  }, [phase, reduced, paused, brand.query, brand.email, brand.phone]);

  // Manual controls — used by the demo controls bar. `next` jumps
  // to the next phase regardless of paused state; `restart` resets
  // to the top of the cycle.
  const PHASE_ORDER: HeroPhase[] = [
    'rebranding',
    'typing',
    'searching',
    'analyzing',
    'result',
    'emailing',
    'success',
  ];
  const goTo = (target: HeroPhase) => {
    // Pre-fill state to match the target phase so jumping in the
    // middle never shows half-typed garbage.
    if (target === 'rebranding' || target === 'typing') {
      setTypedQ('');
    } else {
      setTypedQ(brand.query);
    }
    if (target === 'emailing') {
      setTypedE(brand.email);
      setTypedP('');
    } else if (target === 'success') {
      setTypedE(brand.email);
      setTypedP(brand.phone);
    } else {
      setTypedE('');
      setTypedP('');
    }
    setAnalyzeStep(target === 'result' || target === 'emailing' || target === 'success' ? 2 : 0);
    setCursorStep(0);
    setPhase(target);
  };
  const next = () => {
    const idx = PHASE_ORDER.indexOf(phase);
    if (idx === PHASE_ORDER.length - 1) {
      // From success → next brand, back to rebrand
      setBrandIdx((i) => (i + 1) % DEMO_BRANDS.length);
      setTypedQ('');
      setTypedE('');
      setTypedP('');
      setAnalyzeStep(0);
      setCursorStep(0);
      setPhase('rebranding');
      return;
    }
    goTo(PHASE_ORDER[idx + 1]);
  };
  const prev = () => {
    const idx = PHASE_ORDER.indexOf(phase);
    if (idx === 0) {
      // From rebrand → previous brand, jump to success
      setBrandIdx((i) => (i - 1 + DEMO_BRANDS.length) % DEMO_BRANDS.length);
      setPhase('success');
      return;
    }
    goTo(PHASE_ORDER[idx - 1]);
  };
  const togglePause = () => setPaused((p) => !p);

  return {
    phase,
    typedQ,
    typedE,
    typedP,
    analyzeStep,
    brand,
    brandIdx,
    cursorStep,
    paused,
    next,
    prev,
    togglePause,
  };
}

// Animated cursor that points at the active control. Position is
// measured in % of the embed card so it stays correct on resize.
// Each phase has a target (x%, y%) plus a "clicking" flag — when
// the demo "clicks" we render a soft ripple and shrink the cursor
// briefly before moving on.
// Maps each phase + cursorStep to a `data-cursor-target` value.
// Returning null hides the cursor (rebranding/analyzing). Returning
// 'phone-drift' is a synthetic target that means "drift off to
// the right toward the phone" with no DOM element.
function cursorTargetFor(phase: HeroPhase, cursorStep: number): {
  name: string | null;
  clicking: boolean;
} {
  switch (phase) {
    case 'rebranding':
      return { name: null, clicking: false };
    case 'typing':
      return { name: 'search-input', clicking: false };
    case 'searching':
      return cursorStep >= 1
        ? { name: 'run-button', clicking: true }
        : { name: 'suggestion', clicking: true };
    case 'analyzing':
      return { name: null, clicking: false };
    case 'result':
      if (cursorStep >= 2) return { name: 'stat-row', clicking: false };
      if (cursorStep >= 1) return { name: 'score-rings', clicking: false };
      return { name: 'gbp-card', clicking: false };
    case 'emailing':
      if (cursorStep >= 2) return { name: 'submit-button', clicking: true };
      if (cursorStep >= 1) return { name: 'phone-field', clicking: false };
      return { name: 'email-field', clicking: false };
    case 'success':
      if (cursorStep >= 1) return { name: 'phone-drift', clicking: false };
      return { name: 'submit-button', clicking: true };
    default:
      return { name: null, clicking: false };
  }
}

function HeroDemoCursor({
  phase,
  brand,
  cursorStep,
  cardRef,
}: {
  phase: HeroPhase;
  brand: DemoBrand;
  cursorStep: number;
  cardRef: React.RefObject<HTMLDivElement>;
}) {
  // Position is measured from the actual rendered DOM element with
  // a matching `data-cursor-target` attribute, so the cursor always
  // lands on the real element. Cursor stays mounted at all times —
  // only opacity flips when there's no target to point at — so the
  // cursor never re-mounts from (0,0).
  const target = cursorTargetFor(phase, cursorStep);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [visible, setVisible] = useState(false);
  const [hasMeasuredOnce, setHasMeasuredOnce] = useState(false);
  // Click ripple fires AFTER the cursor settles on the target, not
  // at the moment the target changes. We toggle this on a 0.5s
  // delay (~the spring settle time) so the ripple visually pairs
  // with arrival, not flight.
  const [rippleKey, setRippleKey] = useState(0);
  const [rippleActive, setRippleActive] = useState(false);

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    if (target.name === null) {
      setVisible(false);
      return;
    }
    if (target.name === 'phone-drift') {
      // Synthetic: drift right toward the phone (off-card)
      setPos({ x: 98, y: 60 });
      setVisible(true);
      setHasMeasuredOnce(true);
      return;
    }
    // Measure twice: now + after layout settles. Some elements
    // (gate, fields) animate in via AnimatePresence — by the time
    // their initial scale completes, position may have shifted.
    const measure = () => {
      if (!cardRef.current) return;
      const el = cardRef.current.querySelector(
        `[data-cursor-target="${target.name}"]`
      ) as HTMLElement | null;
      if (!el) {
        setVisible(false);
        return;
      }
      const cardRect = cardRef.current.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const x =
        ((elRect.left + elRect.width / 2 - cardRect.left) / cardRect.width) * 100;
      const y =
        ((elRect.top + elRect.height / 2 - cardRect.top) / cardRect.height) * 100;
      setPos({ x, y });
      setVisible(true);
      setHasMeasuredOnce(true);
    };
    measure();
    const t = setTimeout(measure, 250);
    return () => clearTimeout(t);
  }, [target.name, cardRef, phase, cursorStep]);

  // Trigger the click ripple AFTER cursor settles (spring is ~500ms)
  useEffect(() => {
    if (!target.clicking || !visible) {
      setRippleActive(false);
      return;
    }
    const t = setTimeout(() => {
      setRippleActive(true);
      setRippleKey((k) => k + 1);
    }, 520);
    return () => {
      clearTimeout(t);
      setRippleActive(false);
    };
  }, [target.clicking, visible, target.name, phase, cursorStep]);

  return (
    <motion.div
      className="absolute pointer-events-none z-30"
      // Skip motion's default mount animation so the cursor never
      // animates from (0,0) when first becoming visible.
      initial={false}
      animate={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        opacity: visible && hasMeasuredOnce ? 1 : 0,
      }}
      transition={{
        left: { type: 'spring', stiffness: 60, damping: 16, mass: 0.8 },
        top: { type: 'spring', stiffness: 60, damping: 16, mass: 0.8 },
        // Fade-in waits for spring to mostly settle so the cursor
        // appears AT its target rather than flying in mid-screen.
        opacity: {
          duration: 0.25,
          delay: visible ? 0.45 : 0,
        },
      }}
    >
      {/* Click ripple — fires only after the cursor settles, so the
          ring visually pairs with arrival on the target. */}
      <AnimatePresence>
        {rippleActive && (
          <motion.div
            key={`ring-${rippleKey}`}
            initial={{ scale: 0.4, opacity: 0.7 }}
            animate={{ scale: 2.6, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-2 border-violet-300/80"
          />
        )}
      </AnimatePresence>
      {/* Cursor SVG — classic mac-ish arrow. Path shifted so the
          arrow TIP sits at SVG (0, 0). The motion.div positions
          this SVG by left/top, so the tip — not the corner —
          lands exactly on the target coordinate. Origin set so
          scale animation pivots from the tip too. */}
      <motion.svg
        viewBox="0 0 16 22"
        className="w-5 h-5 drop-shadow-[0_3px_6px_rgba(168,85,247,0.6)]"
        style={{ transformOrigin: '0 0' }}
        animate={{ scale: target.clicking ? 0.85 : 1 }}
        transition={{ duration: 0.18 }}
      >
        <defs>
          <linearGradient id="curGrad" x1="0" y1="0" x2="16" y2="22">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#f5d0fe" />
          </linearGradient>
        </defs>
        <path
          d="M 0 0 L 0 18 L 5.5 12.5 L 9 20 L 12 18.5 L 8.5 11 L 15 11 Z"
          fill="url(#curGrad)"
          stroke="#7c3aed"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </motion.svg>
    </motion.div>
  );
}

// Smartphone mockup styled as an iOS lock screen. Sits to the
// RIGHT of the embed card and represents the agency owner's phone
// — silent and dark while the visitor is filling the form, then a
// brand-tinted notification banner slides down the moment the
// "Get my report" submit fires. Wallpaper, accent colors, and the
// notification "app" all re-skin per active brand so the white-
// label story holds on the receiving end too.
function HeroDemoPhone({
  phase,
  brand,
  brandIdx,
}: {
  phase: HeroPhase;
  brand: DemoBrand;
  brandIdx: number;
}) {
  const showLead = phase === 'success';
  return (
    <div className="relative">
      {/* The phone shell */}
      <div className="relative mx-auto w-[210px] aspect-[9/19.5] rounded-[2rem] border-[7px] border-[#1a1a25] shadow-2xl shadow-black/70 overflow-hidden">
        {/* Wallpaper — animated brand-tinted gradient that swaps with the brand */}
        <motion.div
          key={'wp-' + brand.agency}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
          style={{
            background: `radial-gradient(120% 80% at 50% 0%, ${brand.from}40, transparent 55%), radial-gradient(120% 80% at 50% 100%, ${brand.to}30, transparent 55%), linear-gradient(180deg, #06070d 0%, #0a0b14 50%, #06070d 100%)`,
          }}
        />
        {/* subtle starfield */}
        <div className="absolute inset-0 opacity-50 pointer-events-none">
          {[...Array(14)].map((_, i) => (
            <div
              key={i}
              className="absolute w-px h-px bg-white/40 rounded-full"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 53) % 100}%`,
              }}
            />
          ))}
        </div>

        {/* dynamic island */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[70px] h-[18px] rounded-full bg-black z-20" />

        {/* status bar */}
        <div className="relative z-10 flex items-center justify-between px-4 pt-2.5 pb-1 text-[9px] text-white font-medium">
          <span>9:41</span>
          <div className="flex items-center gap-0.5 text-white/80">
            <span className="text-[8px]">●●●●</span>
            <span className="ml-1 text-[8px]">5G</span>
            <span className="ml-1 text-[8px]">100%</span>
          </div>
        </div>

        {/* notification banner — slides down from top of lock screen on success */}
        <AnimatePresence>
          {showLead && (
            <motion.div
              key={'notif-' + brand.agency}
              initial={{ opacity: 0, y: -50, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              className="absolute top-9 left-2 right-2 z-30 rounded-2xl backdrop-blur-md border p-2.5"
              style={{
                background: 'rgba(20, 20, 28, 0.78)',
                borderColor: `${brand.from}55`,
                boxShadow: `0 12px 30px -8px ${brand.from}50`,
              }}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                {/* "App" icon — agency logo mark inside a tinted
                    rounded-square so it reads like an iOS app icon */}
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${brand.from}30, ${brand.to}10)`,
                    boxShadow: `0 2px 8px ${brand.from}44`,
                    border: `1px solid ${brand.from}40`,
                  }}
                >
                  <BrandLogo
                    brand={brand}
                    brandIdx={brandIdx}
                    size="xs"
                    showWordmark={false}
                  />
                </div>
                <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                  <div
                    className="text-[8.5px] uppercase tracking-wider font-bold truncate"
                    style={{ color: brand.from }}
                  >
                    {brand.agency}
                  </div>
                  <div className="text-[8px] text-white/55 shrink-0">now</div>
                </div>
              </div>
              <div className="text-[10px] text-white font-semibold leading-tight">
                New lead captured
              </div>
              <div className="text-[9.5px] text-white/75 truncate mt-0.5">
                {brand.email}
              </div>
              <div className="text-[9px] text-white/50 truncate mt-0.5">
                {brand.business} · score 73 · PDF attached
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lock-screen center: time + date */}
        <div className="absolute inset-x-0 top-[28%] z-10 text-center text-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/65 font-medium">
            Saturday, May 9
          </div>
          <div className="text-[44px] font-light leading-none tracking-tight mt-1">
            9:41
          </div>
        </div>

        {/* Lock icon */}
        <div className="absolute top-[18%] left-1/2 -translate-x-1/2 z-10 text-white/40">
          <Lock className="h-3 w-3" />
        </div>

        {/* Bottom controls — flashlight + camera, iOS-style */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between z-10">
          <div className="w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white/80">
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
              <path d="M9 2v6h6V2H9zm0 8c-1.1 0-2 .9-2 2v9c0 .55.45 1 1 1h8c.55 0 1-.45 1-1v-9c0-1.1-.9-2-2-2H9z" />
            </svg>
          </div>
          <div className="flex flex-col items-center text-white/45">
            <div className="w-8 h-1 rounded-full bg-white/40" />
          </div>
          <div className="w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white/80">
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="13" r="4" />
              <path d="M5 8h2l1.5-2h7L17 8h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2z" />
            </svg>
          </div>
        </div>

        {/* Subtle "incoming" pulse on success — radial flash from top */}
        <AnimatePresence>
          {showLead && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.6, 0] }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute inset-0 z-20 pointer-events-none"
              style={{
                background: `radial-gradient(60% 35% at 50% 0%, ${brand.from}55, transparent 70%)`,
              }}
            />
          )}
        </AnimatePresence>
      </div>

      {/* "Lead lands here" caption — only visible on success so the
          tie between the form submission and the phone notification
          is unmistakable for non-technical viewers. */}
      <AnimatePresence>
        {showLead && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[9px] font-semibold uppercase tracking-wider whitespace-nowrap z-40"
            style={{
              background: `linear-gradient(135deg, ${brand.from}, ${brand.to})`,
              color: '#fff',
              boxShadow: `0 8px 20px -6px ${brand.from}80`,
            }}
          >
            ↓ Lead lands here
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function HeroAutoDemo({
  phase,
  typedQ,
  typedE,
  typedP,
  analyzeStep,
  brand,
  brandIdx,
  cursorStep,
}: {
  phase: HeroPhase;
  typedQ: string;
  typedE: string;
  typedP: string;
  analyzeStep: number;
  brand: DemoBrand;
  brandIdx: number;
  cursorStep: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={cardRef}
      className="relative rounded-2xl border bg-gradient-to-br from-[#13131c] to-[#0c0c14] shadow-2xl overflow-hidden transition-colors duration-700"
      style={{
        borderColor: `${brand.from}55`,
        boxShadow: `0 25px 80px -20px ${brand.from}50`,
      }}
    >
      <HeroDemoCursor phase={phase} brand={brand} cursorStep={cursorStep} cardRef={cardRef} />
      {/* Embed header — re-skins per brand. Real-looking agency
          logo (icon + wordmark) instead of an initial-square. */}
      <motion.div
        key={brand.agency + '-header'}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: ORGANIC }}
        className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-black/30"
      >
        <BrandLogo brand={brand} brandIdx={brandIdx} size="sm" />
        <div className="ml-auto px-2 py-0.5 rounded text-[9px] font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
          Free
        </div>
      </motion.div>

      {/* Embed-on-domain strip — quietly carries the white-label
          context without a wordy "WHITE-LABEL" pill. The brand
          logos in the header are now doing that work. */}
      <motion.div
        key={'wl-' + brandIdx}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: ORGANIC, delay: 0.15 }}
        className="px-4 py-1.5 flex items-center gap-2 border-b"
        style={{
          background: `linear-gradient(90deg, ${brand.from}12, ${brand.to}06, transparent)`,
          borderColor: `${brand.from}20`,
        }}
      >
        <div
          className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0"
          style={{ background: brand.from }}
        />
        <div className="text-[9px] font-mono text-white/60 truncate">
          {brand.domain}
        </div>
        <div className="ml-auto flex items-center gap-1 text-[8.5px] text-emerald-400 font-semibold">
          <ShieldCheck className="h-2.5 w-2.5" />
          <span>Verified</span>
        </div>
      </motion.div>

      <div className="p-4 min-h-[420px] flex flex-col">
        <AnimatePresence mode="wait">
          {/* REBRANDING — short transition phase that highlights the
              white-label re-skin. Card frame, header, and indicator
              strip have already animated in via their key= bindings;
              this body shows a subtle "Same product. Your brand."
              moment so the visitor explicitly catches the swap. */}
          {phase === 'rebranding' && (
            <motion.div
              key={'rebrand-' + brandIdx}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: ORGANIC }}
              className="flex-1 flex flex-col items-center justify-center text-center"
            >
              <div
                className="text-[9px] uppercase tracking-[0.28em] font-semibold mb-4"
                style={{ color: brand.from }}
              >
                Now serving
              </div>
              {/* Big logo lockup — the white-label payoff. The agency
                  logo gets centre-stage with a soft glow ring. */}
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: ORGANIC, delay: 0.1 }}
                className="relative mb-4"
              >
                <div
                  className="absolute inset-0 -m-4 rounded-full blur-xl"
                  style={{ background: `${brand.from}33` }}
                />
                <div className="relative scale-150">
                  <BrandLogo brand={brand} brandIdx={brandIdx} size="md" />
                </div>
              </motion.div>
              <div className="text-[11px] text-white/65 max-w-[240px] leading-relaxed">
                Same audit engine.{' '}
                <span style={{ color: brand.from }} className="font-semibold">
                  100% {brand.agency}'s brand
                </span>{' '}
                — logo, colors, copy, contact.
              </div>
              {/* Color swatches for the active brand palette */}
              <div className="flex items-center gap-2 mt-4">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: brand.from, boxShadow: `0 0 10px ${brand.from}` }}
                />
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: brand.to, boxShadow: `0 0 10px ${brand.to}` }}
                />
                <div className="text-[9px] text-white/40 font-mono">
                  {brand.from} · {brand.to}
                </div>
              </div>
            </motion.div>
          )}

          {/* TYPING / SEARCHING */}
          {(phase === 'typing' || phase === 'searching') && (
            <motion.div
              key="search"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: ORGANIC }}
            >
              <div className="text-xs font-semibold text-white">Free GBP Audit</div>
              <div className="text-[11px] text-white/50 mt-0.5 mb-4">
                See how you show up on Google in 60 seconds.
              </div>
              <div
                data-cursor-target="search-input"
                className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-[12px] text-white font-mono mb-3 flex items-center min-h-[40px]"
              >
                <span>{typedQ || ''}</span>
                {phase === 'typing' && (
                  <span
                    className="ml-0.5 inline-block w-[2px] h-3.5 animate-pulse"
                    style={{ background: brand.from }}
                  />
                )}
              </div>
              {phase === 'searching' && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-cursor-target="suggestion"
                  className="rounded-lg border border-white/10 bg-black/20 overflow-hidden"
                >
                  <div
                    className="flex items-center gap-2.5 px-3 py-2 border-l-2"
                    style={{
                      background: `${brand.from}15`,
                      borderLeftColor: brand.from,
                    }}
                  >
                    <div
                      className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold"
                      style={{
                        background: `linear-gradient(135deg, ${brand.from}50, ${brand.to}30)`,
                        color: brand.from,
                      }}
                    >
                      {brand.business[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-white font-medium truncate">
                        {brand.business}
                      </div>
                      <div className="text-[10px] text-white/50 truncate">
                        {brand.city}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              <button
                data-cursor-target="run-button"
                disabled
                className="w-full mt-3 py-2.5 rounded-lg text-white text-xs font-semibold flex items-center justify-center gap-2 shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${brand.from}, ${brand.to})`,
                  boxShadow: phase === 'searching' ? `0 8px 20px -6px ${brand.from}80` : 'none',
                  opacity: phase === 'searching' ? 1 : 0.5,
                }}
              >
                Run Free Audit
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          )}

          {/* ANALYZING — spinner does exactly 1.5 rotations then
              the report drops in. No infinite spinning. */}
          {phase === 'analyzing' && (
            <motion.div
              key="analyzing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center text-center"
            >
              <div className="relative mb-5">
                <div
                  className="absolute inset-0 rounded-full blur-xl animate-pulse"
                  style={{ background: `${brand.from}40` }}
                />
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 540 }}
                  transition={{ duration: 1.6, ease: 'linear' }}
                  className="relative w-12 h-12 rounded-full border-2"
                  style={{
                    borderColor: `${brand.from}30`,
                    borderTopColor: brand.from,
                  }}
                />
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2"
                style={{ color: brand.from }}
              >
                Auditing {brand.business}
              </div>
              <div className="text-xs text-white/70 max-w-[230px]">
                {analyzeStep === 0 && 'Fetching profile from Google…'}
                {analyzeStep === 1 && 'Running AI analysis…'}
                {analyzeStep === 2 && 'Generating action plan…'}
              </div>
              <div className="flex items-center gap-1.5 mt-5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-1 rounded-full transition-all"
                    style={{
                      width: i <= analyzeStep ? 28 : 14,
                      background:
                        i <= analyzeStep
                          ? `linear-gradient(90deg, ${brand.from}, ${brand.to})`
                          : 'rgba(255,255,255,0.08)',
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* RESULT + EMAILING + SUCCESS — share a base layer of
              audit content. This mirrors the real GMBAudit flow:
              full audit renders, gets blurred behind a "Unlock the
              full report" gate when the visitor needs to enter
              their email, and finally a "Report Ready" card slides
              in on success. The audit content stays mounted under
              all three phases so the gate visibly covers what's
              underneath instead of swapping screens. */}
          {(phase === 'result' || phase === 'emailing' || phase === 'success') && (
            <motion.div
              key="report"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: ORGANIC }}
              className="relative"
            >
              {/* Audit content base layer — kept compact so the
                  whole flow fits in a 340px card without overflow.
                  Result phase shows it crisp; emailing/success blurs
                  it under the gate. */}
              <motion.div
                animate={{
                  filter: phase === 'result' ? 'blur(0px)' : 'blur(4px)',
                  opacity: phase === 'result' ? 1 : 0.5,
                }}
                transition={{ duration: 0.5, ease: ORGANIC }}
                className="space-y-2.5"
              >
                {/* Real Google Business Profile card — looks like a
                    Google search result for the business. Name, rating,
                    category, hours, address, phone, website. This is
                    what's being audited. */}
                <div
                  data-cursor-target="gbp-card"
                  className="rounded-lg bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 overflow-hidden"
                >
                  {/* Photo strip — 3 mock photos with a subtle tint */}
                  <div className="grid grid-cols-3 gap-px h-12">
                    {[brand.from, brand.to, brand.from].map((c, i) => (
                      <div
                        key={i}
                        className="relative overflow-hidden"
                        style={{
                          background: `linear-gradient(${135 + i * 30}deg, ${c}55, ${c}10)`,
                        }}
                      >
                        <div
                          className="absolute inset-0"
                          style={{
                            background: `radial-gradient(circle at ${30 + i * 20}% ${40 + i * 15}%, ${c}80, transparent 60%)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Body */}
                  <div className="p-2.5">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] text-white font-bold leading-tight truncate">
                          {brand.business}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 text-[9.5px]">
                          <span className="text-amber-400 font-bold tabular-nums">
                            {brand.bizRating.toFixed(1)}
                          </span>
                          <span className="text-amber-400">
                            {'★'.repeat(Math.round(brand.bizRating))}
                            <span className="text-white/20">
                              {'★'.repeat(5 - Math.round(brand.bizRating))}
                            </span>
                          </span>
                          <span className="text-white/45">({brand.bizReviews})</span>
                          <span className="text-white/30">·</span>
                          <span className="text-white/60 truncate">{brand.bizCategory}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/25">
                        <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" />
                        <span className="text-[8.5px] uppercase tracking-wider font-bold text-emerald-300">
                          Audited
                        </span>
                      </div>
                    </div>

                    {/* Quick-info rows: hours, address, phone, website */}
                    <div className="space-y-0.5 mt-1.5 text-[9.5px]">
                      <div className="flex items-center gap-1.5 text-white/70">
                        <Clock className="h-2.5 w-2.5 shrink-0 text-emerald-400" />
                        <span className="truncate">
                          <span className="text-emerald-400 font-semibold">
                            {brand.bizHours.split(' · ')[0]}
                          </span>
                          {brand.bizHours.includes('·') && (
                            <span className="text-white/55">
                              {' · '}
                              {brand.bizHours.split(' · ')[1]}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-white/65">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-2.5 w-2.5 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span className="truncate">{brand.bizAddress}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-white/65">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-2.5 w-2.5 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
                        </svg>
                        <span className="truncate font-mono">{brand.bizPhone}</span>
                      </div>
                      <div className="flex items-center gap-1.5" style={{ color: brand.from }}>
                        <Globe className="h-2.5 w-2.5 shrink-0" />
                        <span className="truncate font-mono">{brand.bizWebsite}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 7 score rings — same set the real audit shows */}
                <div
                  data-cursor-target="score-rings"
                  className="grid grid-cols-7 gap-1 px-1 py-2 rounded-lg bg-black/35 border border-white/5"
                >
                  {[
                    { l: 'Prof', v: 85 },
                    { l: 'SEO', v: 62 },
                    { l: 'Cont', v: 71 },
                    { l: 'Eng', v: 58 },
                    { l: 'Phot', v: 80 },
                    { l: 'Revs', v: 74 },
                    { l: 'Post', v: 68 },
                  ].map((s, i) => {
                    const r = 13;
                    const c = 2 * Math.PI * r;
                    return (
                      <div key={s.l} className="flex flex-col items-center gap-0.5">
                        <div className="relative w-[32px] h-[32px]">
                          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                            <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                            <motion.circle
                              cx="18"
                              cy="18"
                              r={r}
                              fill="none"
                              stroke={`url(#hsg-${brandIdx}-${i})`}
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeDasharray={c}
                              initial={{ strokeDashoffset: c }}
                              animate={{ strokeDashoffset: c * (1 - s.v / 100) }}
                              transition={{ duration: 1, delay: 0.1 + i * 0.06, ease: ORGANIC }}
                            />
                            <defs>
                              <linearGradient id={`hsg-${brandIdx}-${i}`} x1="0" y1="0" x2="36" y2="36">
                                <stop offset="0%" stopColor={brand.from} />
                                <stop offset="100%" stopColor={brand.to} />
                              </linearGradient>
                            </defs>
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white">
                            {s.v}
                          </div>
                        </div>
                        <div className="text-[7.5px] text-white/60 font-semibold">
                          {s.l}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Stat row — issues + quick-wins, plain English */}
                <div
                  data-cursor-target="stat-row"
                  className="grid grid-cols-3 gap-1.5"
                >
                  <div className="px-2 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-center">
                    <div className="text-[14px] text-amber-300 font-bold leading-none">12</div>
                    <div className="text-[8.5px] text-amber-300/80 uppercase tracking-wider font-semibold mt-0.5">
                      Issues
                    </div>
                  </div>
                  <div
                    className="px-2 py-1.5 rounded-md text-center"
                    style={{
                      background: `${brand.from}12`,
                      border: `1px solid ${brand.from}30`,
                    }}
                  >
                    <div className="text-[14px] font-bold leading-none" style={{ color: brand.from }}>
                      8
                    </div>
                    <div
                      className="text-[8.5px] uppercase tracking-wider font-semibold mt-0.5"
                      style={{ color: brand.from }}
                    >
                      Quick wins
                    </div>
                  </div>
                  <div className="px-2 py-1.5 rounded-md bg-rose-500/10 border border-rose-500/20 text-center">
                    <div className="text-[14px] text-rose-300 font-bold leading-none">4</div>
                    <div className="text-[8.5px] text-rose-300/80 uppercase tracking-wider font-semibold mt-0.5">
                      High pri
                    </div>
                  </div>
                </div>

                {/* Two section preview cards — Reviews + Photos
                    (kept to two so layout doesn't overflow) */}
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Star className="h-2.5 w-2.5" style={{ color: brand.from }} />
                      <div className="text-[8.5px] uppercase tracking-wider font-semibold text-white/55">
                        Reviews
                      </div>
                    </div>
                    <div className="text-[10.5px] text-white font-semibold">4.6 ★ · 248</div>
                    <div className="text-[8.5px] text-white/45 truncate">92% response rate</div>
                  </div>
                  <div className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/5">
                    <div className="flex items-center gap-1 mb-0.5">
                      <ImageIcon className="h-2.5 w-2.5" style={{ color: brand.from }} />
                      <div className="text-[8.5px] uppercase tracking-wider font-semibold text-white/55">
                        Photos
                      </div>
                    </div>
                    <div className="text-[10.5px] text-white font-semibold">47 photos</div>
                    <div className="text-[8.5px] text-white/45 truncate">6 categories</div>
                  </div>
                </div>

                {/* Detail teaser bars — fake "more sections below" lines */}
                <div className="space-y-1 pt-0.5">
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full" style={{ background: brand.from }} />
                    <div className="h-1.5 w-3/4 rounded-full bg-white/8" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-white/20" />
                    <div className="h-1.5 w-2/3 rounded-full bg-white/8" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-white/20" />
                    <div className="h-1.5 w-4/5 rounded-full bg-white/8" />
                  </div>
                </div>
              </motion.div>

              {/* UNLOCK GATE — single absolutely-positioned card.
                  Centered vertically. Email phase: lock + email field
                  + button + trust badges. Success phase: same
                  position but content swaps to a clean "Report sent"
                  stamp. Nothing overlaps anything else. */}
              <AnimatePresence>
                {(phase === 'emailing' || phase === 'success') && (
                  <motion.div
                    key="gate"
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.4, ease: ORGANIC }}
                    className="absolute z-20 rounded-xl border-2 backdrop-blur-md"
                    style={{
                      left: 8,
                      right: 8,
                      top: '38%',
                      padding: phase === 'success' ? 12 : 14,
                      background: 'rgba(12, 12, 20, 0.85)',
                      borderColor: phase === 'success' ? '#10b98166' : `${brand.from}66`,
                      boxShadow:
                        phase === 'success'
                          ? '0 16px 40px -10px #10b98166, inset 0 1px 0 #10b98122'
                          : `0 16px 40px -10px ${brand.from}66, inset 0 1px 0 ${brand.from}22`,
                    }}
                  >
                    {phase === 'success' ? (
                      // Clean success card — no Download PDF, no
                      // trust badges, no nested overlays. Just the
                      // single payoff: "Report sent · lead captured".
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center gap-2.5"
                      >
                        <motion.div
                          initial={{ scale: 0.6, rotate: -10 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/40"
                        >
                          <CheckCircle2 className="h-5 w-5 text-white" />
                        </motion.div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] text-white font-bold leading-tight">
                            Report sent · lead captured
                          </div>
                          <div className="text-[9.5px] text-white/60 truncate">
                            Just landed on {brand.agency}'s phone →
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      // Email-gate card
                      <>
                        {/* Lock badge */}
                        <div className="flex items-center justify-center mb-2">
                          <motion.div
                            animate={{
                              boxShadow: [`0 0 0 0 ${brand.from}55`, `0 0 0 8px ${brand.from}00`],
                            }}
                            transition={{ duration: 1.6, repeat: Infinity }}
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${brand.from}, ${brand.to})`,
                            }}
                          >
                            <Lock className="h-4 w-4 text-white" />
                          </motion.div>
                        </div>

                        <div className="text-center text-[12px] text-white font-bold leading-tight mb-0.5">
                          Unlock your full report
                        </div>
                        <div className="text-center text-[9.5px] text-white/65 leading-snug mb-2.5 px-1">
                          Get all 78 data points + 12 action items
                        </div>

                        {/* Email field — active during cursorStep 0 */}
                        <div className="relative mb-2">
                          <motion.div
                            data-cursor-target="email-field"
                            animate={{
                              borderColor:
                                cursorStep === 0 ? brand.from : `${brand.from}55`,
                              boxShadow:
                                cursorStep === 0
                                  ? [`0 0 0 0 ${brand.from}66`, `0 0 0 4px ${brand.from}00`]
                                  : `0 0 0 0 ${brand.from}00`,
                            }}
                            transition={{
                              duration: 1.4,
                              repeat: cursorStep === 0 ? Infinity : 0,
                              ease: 'easeOut',
                            }}
                            className="px-2.5 py-2 rounded-md bg-black/50 border-2 text-[11.5px] text-white font-mono flex items-center min-h-[34px]"
                          >
                            <Mail className="h-3 w-3 mr-1.5 shrink-0" style={{ color: brand.from }} />
                            <span className="truncate">{typedE}</span>
                            {cursorStep === 0 && (
                              <span
                                className="ml-0.5 inline-block w-[2px] h-3 animate-pulse shrink-0"
                                style={{ background: brand.from }}
                              />
                            )}
                          </motion.div>
                        </div>

                        {/* Phone field — active during cursorStep 1.
                            Mock non-routable numbers; matches the
                            real flow (phone required for India geo). */}
                        <div className="relative mb-2.5">
                          <motion.div
                            data-cursor-target="phone-field"
                            animate={{
                              borderColor:
                                cursorStep === 1 ? brand.from : `${brand.from}55`,
                              boxShadow:
                                cursorStep === 1
                                  ? [`0 0 0 0 ${brand.from}66`, `0 0 0 4px ${brand.from}00`]
                                  : `0 0 0 0 ${brand.from}00`,
                            }}
                            transition={{
                              duration: 1.4,
                              repeat: cursorStep === 1 ? Infinity : 0,
                              ease: 'easeOut',
                            }}
                            className="px-2.5 py-2 rounded-md bg-black/50 border-2 text-[11.5px] text-white font-mono flex items-center min-h-[34px]"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-3 w-3 mr-1.5 shrink-0"
                              fill="none"
                              stroke={brand.from}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
                            </svg>
                            <span className="truncate">{typedP}</span>
                            {cursorStep === 1 && (
                              <span
                                className="ml-0.5 inline-block w-[2px] h-3 animate-pulse shrink-0"
                                style={{ background: brand.from }}
                              />
                            )}
                          </motion.div>
                        </div>

                        <motion.button
                          animate={{
                            scale: cursorStep >= 2 ? [1, 1.04, 1] : 1,
                          }}
                          transition={{
                            duration: 0.9,
                            repeat: cursorStep >= 2 ? Infinity : 0,
                          }}
                          className="w-full py-2 rounded-md text-white text-[11px] font-semibold flex items-center justify-center gap-1.5 shadow-lg"
                          style={{
                            background: `linear-gradient(135deg, ${brand.from}, ${brand.to})`,
                            boxShadow: `0 8px 20px -6px ${brand.from}80`,
                          }}
                        >
                          Get Free Report
                          <ArrowRight className="h-3 w-3" />
                        </motion.button>

                        <div className="flex items-center justify-center gap-2 mt-2 text-[8px] text-white/55 font-medium">
                          <span className="flex items-center gap-0.5">
                            <CheckCircle2 className="h-2 w-2 text-emerald-400" />
                            100% free
                          </span>
                          <span className="text-white/20">·</span>
                          <span>PDF report</span>
                          <span className="text-white/20">·</span>
                          <span>No card</span>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Phase commentary strip — large, prominent step indicator
          at the bottom of the embed card. Reads like a guided tour
          caption (Step 3 / 7 + headline + what's happening). Not
          a disclaimer footer. */}
      <div
        className="px-4 py-3 border-t-2 flex items-center gap-3 relative overflow-hidden"
        style={{
          borderColor: `${brand.from}40`,
          background: `linear-gradient(90deg, ${brand.from}1a, ${brand.to}10, transparent)`,
        }}
      >
        {/* Step number badge */}
        <div
          className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${brand.from}, ${brand.to})`,
            boxShadow: `0 4px 12px -4px ${brand.from}80`,
          }}
        >
          <span className="text-[14px]">
            {(['rebranding', 'typing', 'searching', 'analyzing', 'result', 'emailing', 'success'] as HeroPhase[]).indexOf(phase) + 1}
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={'narr-' + phase}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.3 }}
            className="min-w-0 flex-1"
          >
            <div
              className="text-[8.5px] uppercase tracking-[0.18em] font-bold mb-0.5"
              style={{ color: brand.from }}
            >
              {phase === 'rebranding' && 'White-label re-skin'}
              {phase === 'typing' && 'Step 1 · Visitor lands'}
              {phase === 'searching' && 'Step 2 · Business found'}
              {phase === 'analyzing' && 'Step 3 · AI is auditing'}
              {phase === 'result' && 'Step 4 · Audit revealed'}
              {phase === 'emailing' && 'Step 5 · Lead capture'}
              {phase === 'success' && '✓ Lead captured live'}
            </div>
            <div className="text-[12.5px] text-white font-semibold leading-tight">
              {phase === 'rebranding' && (
                <>
                  Same engine, now branded as{' '}
                  <span style={{ color: brand.from }} className="font-bold">
                    {brand.agency}
                  </span>
                </>
              )}
              {phase === 'typing' && <>They search their business by name</>}
              {phase === 'searching' && <>One tap picks the right Google profile</>}
              {phase === 'analyzing' && <>78 data points scanned in seconds</>}
              {phase === 'result' && <>Free score reveals issues + quick wins</>}
              {phase === 'emailing' && (
                <>
                  Email + phone unlocks the full PDF report
                </>
              )}
              {phase === 'success' && (
                <>
                  Lead just landed on{' '}
                  <span style={{ color: brand.from }} className="font-bold">
                    {brand.agency}
                  </span>
                  's phone →
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SECTION 1 — Hero (more dynamic: scroll-fade, richer mockup,
// rotating lead toasts, trust strip)
// ─────────────────────────────────────────────────────────────────
function Hero({ scrollToDemo }: { scrollToDemo: () => void }) {
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const parallaxRef = useMouseParallax(reduced);
  const heroRef = useRef<HTMLDivElement>(null);

  // The auto-demo state machine is intentionally NOT started. The launch film
  // replaced that demo in the hero, but the hook kept running: timers firing
  // and state updating every cycle to drive components nothing rendered. The
  // components themselves stay in this file (tree-shaking keeps them out of
  // the bundle) so the demo can be restored by re-adding this call and
  // rendering HeroAutoDemo / DemoControls / HeroDemoPhone in the visual column.

  // Scroll-driven hero fade — content fades + scales as user scrolls,
  // giving the next section its own moment instead of fighting the hero.
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.6, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-gradient-to-b from-gray-950 via-[#0c0c14] to-[#15161D]"
    >
      <motion.div
        ref={parallaxRef}
        style={{
          opacity: heroOpacity,
          scale: heroScale,
          ['--p1x' as string]: 0,
          ['--p1y' as string]: 0,
          ['--p2x' as string]: 0,
          ['--p2y' as string]: 0,
          ['--p3x' as string]: 0,
          ['--p3y' as string]: 0,
        }}
        className="absolute inset-0"
      >
        {/* Layer 3 — gradient blobs */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform:
              'translate3d(calc(var(--p3x) * -24px), calc(var(--p3y) * -24px), 0)',
          }}
        >
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-violet-600/25 blur-3xl" />
          <div className="absolute top-1/3 -right-40 w-[700px] h-[700px] rounded-full bg-fuchsia-600/20 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 w-[500px] h-[500px] rounded-full bg-pink-600/10 blur-3xl" />
        </div>

        {/* Layer 2 — particles */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform:
              'translate3d(calc(var(--p2x) * -12px), calc(var(--p2y) * -12px), 0)',
          }}
        >
          {Array.from({ length: 36 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/30"
              style={{
                width: i % 5 === 0 ? '2px' : '1px',
                height: i % 5 === 0 ? '2px' : '1px',
                left: `${(i * 37) % 100}%`,
                top: `${(i * 67) % 100}%`,
                animation: `drift ${8 + (i % 6)}s ease-in-out ${i * 0.3}s infinite alternate`,
              }}
            />
          ))}
        </div>

        {/* Grid overlay — subtle tech feel */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />

        <div className="relative container mx-auto px-6 lg:px-8 pt-32 pb-20 lg:pt-36 lg:pb-20 grid lg:grid-cols-[1fr_1.5fr] gap-12 lg:gap-12 items-center min-h-screen">
          {/* Copy column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: ORGANIC }}
            className="relative z-10"
          >
            {/* No kicker over the headline (impeccable pass, 17 Oct 26): the
                audience it named now opens the lead line under the heading. */}
            <div className="relative">
              <LeadStreamBackdrop />
              <h1
                className="relative z-10 text-white font-bold tracking-tight leading-[0.95]"
                style={{ fontSize: 'clamp(3rem, 7.5vw, 6.4rem)' }}
              >
                {/* The trailing space is load-bearing: the two spans stack
                    visually either way, but without it the accessible name and
                    the text a crawler reads for the site's most important
                    heading are the single token "MoreLeads". */}
                <span className="block">More </span>
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-violet-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent">
                    Leads
                  </span>
                  <span
                    className="absolute -bottom-2 left-0 right-0 h-4 bg-gradient-to-r from-violet-500/40 to-fuchsia-500/40 blur-xl"
                    aria-hidden
                  />
                </span>
              </h1>
            </div>

            {/* Names both jobs, not just the embed. The outbound one (audit a
                business you are pitching) was absent from the page entirely,
                even though it is what most agencies reach for first. */}
            <p className="mt-6 text-lg md:text-xl text-white/70 leading-relaxed max-w-md">
              Put a free audit on your agency's site and visitors hand you their{' '}
              <span className="text-white font-semibold">contact details</span> to get the report. Or run the same audit on a business you are pitching, and walk in already knowing what is broken. Your brand on every report.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <MagneticButton
                onClick={() => navigate('/pricing')}
                className="group relative px-7 h-14 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold shadow-2xl shadow-fuchsia-500/30 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center gap-2 text-base">
                  Get my free embed
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </MagneticButton>
              {/* "See it in action" jumps the visitor straight into the
                  live GBP Audit tool — the most "wow" demo we have shipped
                  and reachable from the funnel. */}
              <a href="/tools/gmb-audit">
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-white border border-white/15 hover:bg-white/5 hover:text-white h-14 px-6 rounded-xl backdrop-blur"
                >
                  <PlayCircle className="mr-2 h-4 w-4" />
                  See it in action
                </Button>
              </a>
            </div>

            {/* The agent for owners, one line under the CTAs (WO-I1, Anand,
                14 Oct 26: the landing linked from the home page's hero). */}
            <AgentLandingLink
              before="Run a local business?"
              anchor={AGENT_LINK_ANCHORS.home}
              className="mt-5 text-sm text-white/60"
              linkClassName="font-semibold text-white/90 underline decoration-violet-400/60 underline-offset-4 transition-colors hover:text-white"
            />

            {/* Trust strip — micro-proof under the CTAs */}
            <div className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-white/50">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                <span>No credit card</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-violet-400" />
                <span>Live in 2 min</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-fuchsia-400" />
                <span>White-label included</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-emerald-400" />
                <span>
                  Leads <span className="text-white/80 font-semibold">only</span> ever go to your inbox
                </span>
              </div>
            </div>
          </motion.div>

          {/* Visual column — auto-playing mini demo loop */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: ORGANIC, delay: 0.15 }}
            className="relative"
          >
            {/* TEMPORARY LOCAL PREVIEW ONLY — the auto-playing demo has been
                swapped for the launch video so it can be reviewed in context.
                Revert with:
                  git checkout src/pages/LeadGenToolsLanding.tsx
                  rm public/fly-launch-video.mp4

                The film is 16:9 and the column is portrait, so the frame owns
                the aspect ratio (aspect-video) and the video fills it edge to
                edge with nothing cropped. It is vertically centred and allowed
                to bleed past the right edge on large screens, which is how
                product launch pages give a film presence without shrinking it
                to a postage stamp. Muted + playsInline are what actually permit
                autoplay in Safari and on iOS. */}
            <LaunchVideo />
          </motion.div>
        </div>
      </motion.div>

      <style>{`
        @keyframes drift {
          0% { transform: translate(0, 0); opacity: 0.2; }
          100% { transform: translate(20px, -20px); opacity: 0.7; }
        }
      `}</style>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────
// SECTION 2 — Pain frames (richer, with motion inside frames)
// ─────────────────────────────────────────────────────────────────
// PainFrames + horizontal-scroll section removed — felt slow and
// got in the way of users reaching the demo. Cleaner narrative
// without it: hero → trust strip → value triplet → demo.

// ─────────────────────────────────────────────────────────────────
// SECTION 3 — Value triplet (richer cards w/ stats + glow)
// ─────────────────────────────────────────────────────────────────
function ValueTriplet() {
  // Plain-English benefits — written so a non-technical agency
  // owner gets it immediately, no jargon, no abstract claims.
  // Each card has a clear before → after shift.
  const cards = [
    {
      title: 'Leads come to you',
      before: 'You chase people.',
      after: 'They come to you.',
      body:
        'A visitor comes to your website and runs a free audit. You get an email notification with their contact details and their audit report. So your first conversation starts warm, not from scratch.',
      accent: 'from-violet-500 to-blue-500',
      icon: <Inbox className="h-5 w-5" />,
      stat: { value: '4.7×', label: 'more leads' },
    },
    {
      title: 'It looks like your agency',
      before: 'Someone else\'s tool.',
      after: 'A page that looks like yours.',
      body:
        'The tool sits on your website. Your logo, your colours, and your contact details show on every screen. Reports go out with your branding. To the visitor, it is your agency\'s tool. Fly is never shown.',
      accent: 'from-fuchsia-500 to-pink-500',
      icon: <Award className="h-5 w-5" />,
      stat: { value: '100%', label: 'your site, your brand' },
    },
    {
      title: 'Turn visitors into leads',
      before: 'Visitors come and go.',
      after: 'They leave their details.',
      body:
        'Most people land on your site and leave without a trace. A free audit gives them a reason to stay, and each one hands you their contact details. So you build a list of people who already want help, ready for you to reach out to.',
      accent: 'from-violet-500 to-fuchsia-500',
      icon: <TrendingUp className="h-5 w-5" />,
      stat: { value: 'Every', label: 'interested visitor, captured' },
    },
  ];
  return (
    <section className="relative bg-gray-950 py-24 lg:py-28">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: ORGANIC }}
          className="max-w-2xl mb-14"
        >
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', lineHeight: 1.15 }}
          >
            Here's what you get.
          </h2>
          <p className="mt-3 text-base text-white/60 max-w-xl">
            What changes for your agency once your tools are live.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: ORGANIC }}
            >
              <TiltCard className="h-full" glow>
                <div
                  className="relative h-full p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#13131c] to-[#0c0c14] overflow-hidden"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle at var(--gx, 50%) var(--gy, 50%), rgba(168,85,247,0.10), transparent 40%)',
                  }}
                >
                  <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center text-white shadow-lg mb-5`}
                  >
                    {c.icon}
                  </div>
                  <h3 className="text-white text-xl font-bold tracking-tight mb-3">
                    {c.title}
                  </h3>
                  {/* Before → after micro-row — anyone gets it at a glance */}
                  <div className="flex items-center gap-2 text-[11px] mb-4">
                    <span className="px-2 py-0.5 rounded bg-rose-500/12 text-rose-300/80 line-through decoration-rose-400/40">
                      {c.before}
                    </span>
                    <ArrowRight className="h-3 w-3 text-white/40 shrink-0" />
                    <span className="px-2 py-0.5 rounded bg-emerald-500/12 text-emerald-300 font-semibold">
                      {c.after}
                    </span>
                  </div>
                  <p className="text-[13.5px] text-white/65 leading-relaxed mb-5">
                    {c.body}
                  </p>
                  <div className="pt-4 border-t border-white/8 flex items-baseline gap-2">
                    <div
                      className="text-xl font-bold text-violet-200 tabular-nums"
                    >
                      {c.stat.value}
                    </div>
                    <div className="text-[11px] text-white/50">{c.stat.label}</div>
                  </div>
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// SECTION — Tools suite (live + coming soon).
// Agencies need to see the full menu, not just GBP audit. Coming-
// soon tools are clearly badged so nobody thinks they're paying
// for vapor; live ones are visually weighted to read as "ready
// today". Plain-English descriptions for non-tech viewers.
// ─────────────────────────────────────────────────────────────────
function ToolsSuite() {
  // Each card links to that tool's own public page so visitors can try it.
  const TOOL_HREF: Record<string, string> = {
    'Google Business Profile Audit': '/tools/gmb-audit',
    'Local Rank Checker': '/tools/local-rank-check',
    'Review Analysis': '/tools/review-analysis',
    'AI GBP Description Generator': '/tools/gbp-description-generator',
    'AI Review Response Generator': '/tools/review-response-generator',
    'AI Caption Generator': '/tools/caption-generator',
    '90-day Content Calendar Generator': '/tools/content-calendar-generator',
    'LinkedIn Profile Audit': '/tools/linkedin-audit',
    'Instagram Profile Audit': '/tools/instagram-audit',
    'Google Posts Activity Score': '/tools/gbp-posts-score',
    'Restaurant Profile Audit': '/tools/restaurant-audit',
    'SEO Site Audit': '/tools/seo-site-audit',
    'AI Search Visibility': '/tools/ai-search-visibility',
  };
  const tools = [
    {
      name: 'Google Business Profile Audit',
      tagline: 'A full check of any Google profile',
      body:
        'It checks a business’s Google profile (photos, reviews, posts, hours and more) and gives a score with a simple list of what to fix. The visitor sees how to rank higher on Google. You get them as a lead.',
      icon: <Star className="h-5 w-5" />,
      accent: 'from-violet-500 to-fuchsia-500',
      status: 'live' as const,
    },
    {
      name: 'Local Rank Checker',
      tagline: 'See where a business shows up on Google Maps',
      body:
        'It shows where a business appears on Google Maps for a search, right across their area. The visitor sees where they rank and where rivals beat them. You get them as a lead.',
      icon: <MapPin className="h-5 w-5" />,
      accent: 'from-cyan-500 to-blue-500',
      status: 'live' as const,
    },
    {
      name: 'Review Analysis',
      tagline: 'See what customers really think',
      body:
        'It reads a business’s Google reviews and sums up what people love, what they complain about, and what is changing. The visitor learns how customers really feel. You get them as a lead.',
      icon: <Sparkles className="h-5 w-5" />,
      accent: 'from-amber-500 to-rose-500',
      status: 'live' as const,
    },
    {
      name: 'AI GBP Description Generator',
      tagline: 'A ready-to-use profile description',
      body:
        'It writes a description for a Google profile, with the city, the service and a clear next step. The visitor just copies and pastes it. You get them as a lead.',
      icon: <FileText className="h-5 w-5" />,
      accent: 'from-blue-500 to-cyan-500',
      status: 'live' as const,
    },
    {
      name: 'AI Review Response Generator',
      tagline: 'Quick, friendly replies to reviews',
      body:
        'It writes warm, professional replies to Google reviews in seconds. The visitor replies to customers without the effort. You get them as a lead.',
      icon: <MessageSquare className="h-5 w-5" />,
      accent: 'from-rose-500 to-pink-500',
      status: 'live' as const,
    },
    {
      name: 'AI Caption Generator',
      tagline: 'Ready-to-post social captions',
      body:
        'It writes social media captions, with hashtags, in the business’s own voice. The visitor saves hours on content. You get them as a lead.',
      icon: <PenLine className="h-5 w-5" />,
      accent: 'from-pink-500 to-orange-500',
      status: 'live' as const,
    },
    {
      name: '90-day Content Calendar Generator',
      tagline: 'Three months of posts, planned',
      body:
        'It plans three months of social posts: topics, captions, hashtags and image ideas. The visitor gets a full plan instead of a blank page. You get them as a lead.',
      icon: <Calendar className="h-5 w-5" />,
      accent: 'from-emerald-500 to-teal-500',
      status: 'live' as const,
    },
    {
      name: 'LinkedIn Profile Audit',
      tagline: 'Make any LinkedIn profile stronger',
      body:
        'It checks a LinkedIn profile (the headline, About and posts) and suggests clear improvements. The visitor gets a stronger profile. You get them as a lead.',
      icon: <Linkedin className="h-5 w-5" />,
      accent: 'from-sky-500 to-blue-600',
      status: 'live' as const,
    },
    {
      name: 'Instagram Profile Audit',
      tagline: 'See how an Instagram profile measures up',
      body:
        'It scores an Instagram profile against others in the same industry and gives a bio rewrite, hashtag tips and a 30-day plan. The visitor sees how to grow. You get them as a lead.',
      icon: <Instagram className="h-5 w-5" />,
      accent: 'from-fuchsia-500 to-purple-600',
      status: 'live' as const,
    },
    {
      name: 'Google Posts Activity Score',
      tagline: 'Check how often they post on Google',
      body:
        'It checks how often a business posts on Google and gives them a 30-day posting plan. The visitor learns what to post and when. You get them as a lead.',
      icon: <TrendingUp className="h-5 w-5" />,
      accent: 'from-emerald-500 to-lime-500',
      status: 'live' as const,
    },
    {
      name: 'Restaurant Profile Audit',
      tagline: 'A Google check made for restaurants',
      body:
        'It checks a restaurant’s Google profile (menu link, food photos, bookings, delivery and ratings) and shows what to fix. The visitor sees exactly what is missing. You get them as a lead.',
      icon: <Utensils className="h-5 w-5" />,
      accent: 'from-orange-500 to-red-500',
      status: 'live' as const,
    },
    {
      name: 'SEO Site Audit',
      tagline: 'A technical health check for any website',
      body:
        'It crawls up to 25 pages of a website and scores its technical SEO health, then lists every error, warning, and notice with a prioritised fix list. The visitor sees exactly what is holding their site back in search. You get them as a lead.',
      icon: <Gauge className="h-5 w-5" />,
      accent: 'from-teal-500 to-cyan-500',
      status: 'live' as const,
    },
    {
      name: 'AI Search Visibility',
      tagline: 'See if AI engines recommend the business',
      body:
        'It runs real buying-intent prompts through ChatGPT, Gemini, Claude, and Perplexity, then shows how often the business is mentioned, who gets recommended instead, and a competitor leaderboard. The visitor sees their AI search presence. You get them as a lead.',
      icon: <Bot className="h-5 w-5" />,
      accent: 'from-indigo-500 to-fuchsia-500',
      status: 'live' as const,
    },
  ];

  return (
    <section className="relative bg-gradient-to-b from-[#0d0d14] via-gray-950 to-[#0d0d14] py-24 lg:py-28 border-t border-white/5">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: ORGANIC }}
          className="max-w-2xl mb-12"
        >
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', lineHeight: 1.15 }}
          >
            Thirteen tools live today. More shipping soon.
          </h2>
          <p className="mt-3 text-base text-white/60 max-w-xl">
            Free tools for your visitors: profile audits, AI writers and
            planners. One embed code, one lead inbox, your brand. Every new
            tool we ship lands in your account automatically.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((t, i) => {
            const isLive = t.status === 'live';
            return (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: ORGANIC }}
                className={`relative p-7 rounded-2xl border transition-colors ${
                  isLive
                    ? 'border-white/10 bg-gradient-to-br from-[#13131c] to-[#0c0c14] hover:border-white/20'
                    : 'border-white/5 bg-white/[0.02] opacity-90'
                }`}
              >
                {/* Status badge */}
                <div className="absolute top-5 right-5">
                  {isLive ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10.5px] font-bold uppercase tracking-wider text-emerald-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Live
                    </div>
                  ) : (
                    <div className="px-2.5 py-1 rounded-full bg-amber-500/12 border border-amber-500/25 text-[10.5px] font-bold uppercase tracking-wider text-amber-300">
                      Coming soon
                    </div>
                  )}
                </div>

                {/* Icon */}
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${t.accent} flex items-center justify-center text-white shadow-lg mb-5 ${
                    !isLive ? 'opacity-70' : ''
                  }`}
                >
                  <span className="[&>svg]:h-6 [&>svg]:w-6">{t.icon}</span>
                </div>

                <h3 className="text-white text-xl font-bold tracking-tight mb-1.5">
                  {t.name}
                </h3>
                <div className="text-[13px] text-white/55 font-medium mb-3.5">
                  {t.tagline}
                </div>
                <p className="text-[14.5px] text-white/70 leading-relaxed">
                  {t.body}
                </p>
                <a
                  href={TOOL_HREF[t.name]}
                  className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-105"
                >
                  Check it out
                  <ArrowRight className="h-4 w-4" />
                </a>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// SECTION 3.5 — Before / After comparison (NEW — high conversion)
// ─────────────────────────────────────────────────────────────────
function BeforeAfter() {
  return (
    <section className="relative bg-gradient-to-b from-gray-950 to-[#0d0d14] py-24 lg:py-32">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: ORGANIC }}
          className="max-w-2xl mb-12"
        >
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.15 }}
          >
            What changes when you add Fly.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: ORGANIC }}
            className="p-7 rounded-2xl border border-white/10 bg-[#0e0e16]"
          >
            <div className="text-xs uppercase tracking-wider text-red-400/80 font-semibold mb-5 flex items-center gap-2">
              <Minus className="h-3 w-3" />
              Before Fly
            </div>
            <ul className="space-y-4 text-white/70">
              {[
                'Traffic comes to your site, then leaves without a trace',
                'Running ads for leads, but barely any convert',
                'Leads scattered across Excel and Google Sheets',
                'Hoping SEO or ads finally work next month',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400/60 shrink-0" />
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: ORGANIC, delay: 0.1 }}
            className="p-7 rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/[0.08] via-[#13131c] to-[#0c0c14] shadow-2xl shadow-violet-500/10"
          >
            <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-5 flex items-center gap-2">
              <Plus className="h-3 w-3" />
              After Fly
            </div>
            <ul className="space-y-4 text-white">
              {[
                'Visitors leave their details to get a free audit',
                'Your own website turns traffic into leads',
                'Every lead saved in one place, ready to follow up',
                'A steady flow of interested people every month',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400 shrink-0" />
                  <span className="text-[15px] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// SECTION 4 — Live demo (now ACTUALLY playable: search → audit →
// score reveal → email capture → success → lead lands on phone)
// All mock data, no real API. The flow mirrors the real GMB Audit
// embed product end-to-end so a prospect can feel the entire
// customer journey in 30 seconds without leaving the page.
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// SECTION 5 — How it works (richer step stages)
// ─────────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Pick your tools.',
      desc: 'Audit, rank tracker, review insights: turn on the ones that fit your offer.',
      icon: <Sparkles className="h-5 w-5" />,
    },
    {
      n: '02',
      title: 'Make them yours.',
      desc: 'Logo, colors, contact info. Two minutes. The visitor sees only your brand.',
      icon: <PaintBucket className="h-5 w-5" />,
    },
    {
      n: '03',
      title: 'Drop into your site.',
      desc: 'A small snippet. Paste it once. Your website starts working the moment it loads.',
      icon: <Code2 className="h-5 w-5" />,
    },
  ];
  return (
    <section className="relative bg-gradient-to-b from-[#0d0d14] to-gray-950 py-32 lg:py-40">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: ORGANIC }}
          className="max-w-2xl mb-20"
        >
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.4rem)', lineHeight: 1.1 }}
          >
            Three steps. No engineer required.
          </h2>
        </motion.div>

        <div className="space-y-24">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, ease: ORGANIC, delay: i * 0.05 }}
              className="grid lg:grid-cols-2 gap-10 items-center"
            >
              <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30 flex items-center justify-center text-violet-300">
                    {s.icon}
                  </div>
                  <div className="text-7xl font-bold text-violet-300/80 leading-none" aria-hidden>
                    {s.n}
                  </div>
                </div>
                <h3 className="text-white text-3xl lg:text-4xl font-bold tracking-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-lg text-white/60 max-w-md">{s.desc}</p>
              </div>
              <div
                className={`relative h-[360px] flex items-center justify-center ${
                  i % 2 === 1 ? 'lg:order-1' : ''
                }`}
              >
                <StepStage stepIndex={i} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Interactive — click cards to toggle Selected / Add. Sells the
// "you're in control" feeling at first contact.
// Step 1 — pick the tools to enable. Clean vertical toggle list:
// click a row to flip its switch. Selected rows light up with brand
// gradient + check; unselected stay muted. Matches the real
// dashboard UX where agency owners turn tools on/off per page.
function Step01Picker() {
  const [picked, setPicked] = useState<Record<string, boolean>>({
    'Google Business Profile Audit': true,
    'Local Rank Checker': false,
    'Review Analysis': true,
    'Content Idea Generator': false,
  });
  const tools = [
    {
      name: 'Google Business Profile Audit',
      sub: 'AI audit of any Google Business Profile',
      accent: 'from-violet-500 to-fuchsia-500',
      icon: <Star className="h-4 w-4" />,
      live: true,
    },
    {
      name: 'Local Rank Checker',
      sub: 'See where a business ranks on Google Maps',
      accent: 'from-blue-500 to-cyan-500',
      icon: <MapPin className="h-4 w-4" />,
      live: false,
    },
    {
      name: 'Review Analysis',
      sub: 'AI sentiment + trends across reviews',
      accent: 'from-amber-500 to-rose-500',
      icon: <Sparkles className="h-4 w-4" />,
      live: false,
    },
    {
      name: 'Content Idea Generator',
      sub: '30+ post ideas tailored to a niche',
      accent: 'from-emerald-500 to-lime-500',
      icon: <Lightbulb className="h-4 w-4" />,
      live: false,
    },
  ];
  const enabledCount = Object.values(picked).filter(Boolean).length;
  return (
    <div className="w-full max-w-md mx-auto space-y-2.5">
      {/* Header strip — live count of enabled tools */}
      <div className="flex items-center justify-between px-1 mb-1">
        <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
          Tools on this page
        </div>
        <div className="flex items-center gap-1 text-[11px] text-emerald-300 font-bold tabular-nums">
          <CheckCircle2 className="h-3 w-3" />
          {enabledCount} enabled
        </div>
      </div>

      {tools.map((c, i) => {
        const isPicked = picked[c.name];
        return (
          <motion.button
            key={c.name}
            type="button"
            onClick={() => setPicked((p) => ({ ...p, [c.name]: !p[c.name] }))}
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.08, ease: ORGANIC }}
            whileHover={{ x: 2 }}
            whileTap={{ scale: 0.98 }}
            className={`group w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
              isPicked
                ? 'border-violet-400/40 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/5 shadow-md shadow-violet-500/10'
                : 'border-white/8 bg-white/[0.02] hover:bg-white/[0.04]'
            }`}
          >
            {/* Icon */}
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0 transition-all ${
                isPicked
                  ? `bg-gradient-to-br ${c.accent} shadow-md`
                  : 'bg-white/[0.06] text-white/45'
              }`}
            >
              {c.icon}
            </div>

            {/* Name + sub */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <div
                  className={`text-[13px] font-semibold truncate ${
                    isPicked ? 'text-white' : 'text-white/70'
                  }`}
                >
                  {c.name}
                </div>
                {c.live && (
                  <span className="px-1.5 py-0 rounded text-[8.5px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 shrink-0">
                    Live
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-white/45 truncate">{c.sub}</div>
            </div>

            {/* iOS-style toggle switch */}
            <div
              className={`relative w-9 h-[22px] rounded-full transition-colors shrink-0 ${
                isPicked ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500' : 'bg-white/10'
              }`}
            >
              <motion.div
                animate={{ x: isPicked ? 18 : 2 }}
                transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-md"
              />
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

// Interactive — pick a brand palette, edit the contact email/phone,
// and watch the preview button + lead notification re-skin live.
function Step02Brander() {
  const PALETTES = [
    { from: '#a855f7', to: '#ec4899', name: 'Violet' },
    { from: '#3b82f6', to: '#06b6d4', name: 'Sapphire' },
    { from: '#10b981', to: '#84cc16', name: 'Emerald' },
    { from: '#f59e0b', to: '#ef4444', name: 'Sunset' },
  ];
  const [pi, setPi] = useState(0);
  const [name, setName] = useState('Your Agency');
  const [email, setEmail] = useState('hello@amplifydigital.in');
  const [phone, setPhone] = useState('+91 98765 43210');
  const palette = PALETTES[pi];

  return (
    <div className="relative w-full max-w-md">
      <div className="p-7 rounded-2xl border border-white/10 bg-[#0e0e16] shadow-2xl">
        {/* Agency logo + name — the first thing an agency sets up */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
            Your logo &amp; agency name
          </div>
          <div className="px-2 py-0.5 rounded text-[9px] font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
            Auto-saved
          </div>
        </div>
        <div className="flex items-center gap-3 mb-5">
          {/* Logo upload */}
          <motion.button
            type="button"
            aria-label="Upload your logo"
            key={palette.name}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: ORGANIC }}
            className="group relative w-12 h-12 rounded-xl shadow-lg shrink-0"
            style={{
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              boxShadow: `0 8px 24px -8px ${palette.from}80`,
            }}
          >
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#0e0e16] border border-white/15 flex items-center justify-center">
              <ImageIcon className="h-2.5 w-2.5 text-white/70" />
            </span>
          </motion.button>
          {/* Agency name — type to edit */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-white/30 transition-colors">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-transparent text-[12px] text-white font-semibold outline-none placeholder:text-white/30"
                placeholder="Your agency name"
              />
            </div>
            <div className="text-[10px] text-white/40 font-mono mt-1">amplifydigital.in</div>
          </div>
        </div>

        {/* Color picker — click to switch */}
        <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">
          Brand colors
        </div>
        <div className="flex gap-2 mb-5">
          {PALETTES.map((p, i) => (
            <button
              key={p.name}
              type="button"
              onClick={() => setPi(i)}
              aria-label={`Pick ${p.name}`}
              className={`w-9 h-9 rounded-lg transition-all ${
                i === pi
                  ? 'ring-2 ring-white/50 ring-offset-2 ring-offset-[#0e0e16] scale-110'
                  : 'hover:scale-110'
              }`}
              style={{
                background: `linear-gradient(135deg, ${p.from}, ${p.to})`,
              }}
            />
          ))}
        </div>

        {/* Inline-editable contact info */}
        <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">
          Contact CTA · type to edit
        </div>
        <div className="space-y-2 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-white/30 transition-colors">
            <Mail className="h-3.5 w-3.5 text-white/40 shrink-0" />
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent text-[11px] text-white/80 font-mono outline-none placeholder:text-white/30"
              placeholder="hello@your-agency.com"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-white/30 transition-colors">
            <Globe className="h-3.5 w-3.5 text-white/40 shrink-0" />
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="flex-1 bg-transparent text-[11px] text-white/80 font-mono outline-none placeholder:text-white/30"
              placeholder="+91 98765 43210"
            />
          </div>
        </div>

        {/* Live preview */}
        <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">
          Live preview
        </div>
        <button
          type="button"
          className="w-full py-3 rounded-lg text-white text-sm font-semibold relative overflow-hidden group transition-all"
          style={{
            background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
            boxShadow: `0 10px 24px -10px ${palette.from}80`,
          }}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          <span className="relative">Run Free Audit</span>
        </button>

        {/* Mini lead-card preview that also re-skins */}
        <motion.div
          key={palette.name + email}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: ORGANIC }}
          className="mt-3 p-3 rounded-lg border flex items-center gap-2.5"
          style={{
            background: `linear-gradient(135deg, ${palette.from}10, ${palette.to}08)`,
            borderColor: `${palette.from}40`,
          }}
        >
          <div
            className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${palette.from}30, ${palette.to}20)`,
            }}
          >
            <Mail className="h-3 w-3" style={{ color: palette.from }} />
          </div>
          <div className="min-w-0">
            <div
              className="text-[9px] uppercase tracking-wider font-semibold"
              style={{ color: palette.from }}
            >
              Lead notification preview
            </div>
            <div className="text-[10px] text-white/70 truncate">
              From: <span className="font-mono text-white/90">{email}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Helper hint to nudge interaction */}
      <div className="absolute -top-3 right-4 px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-500/30 text-[10px] text-violet-200 font-medium flex items-center gap-1.5">
        <Sparkles className="h-3 w-3" />
        Try it: pick a color
      </div>
    </div>
  );
}

function StepStage({ stepIndex }: { stepIndex: number }) {
  if (stepIndex === 0) return <Step01Picker />;
  if (stepIndex === 1) return <Step02Brander />;
  return (
    <div className="relative w-full max-w-md">
      <div className="rounded-2xl border border-white/10 bg-[#08080d] overflow-hidden font-mono text-[11px]">
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/40">
          <div className="w-2 h-2 rounded-full bg-red-500/60" />
          <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
          <div className="w-2 h-2 rounded-full bg-green-500/60" />
          <div className="ml-3 text-[10px] text-white/40">index.html</div>
        </div>
        <div className="p-5 text-white/60 leading-relaxed">
          <div className="text-white/30 mb-1">{'<!-- Fly Lead Gen -->'}</div>
          <div>
            <span className="text-fuchsia-400">{'<script'}</span>{' '}
            <span className="text-emerald-400">src</span>=
            <span className="text-amber-300">"https://www.fly-social.com/embed.js"</span>
          </div>
          <div className="ml-4">
            <span className="text-emerald-400">data-agency</span>=
            <span className="text-amber-300">"amplifydigital"</span>
          </div>
          <div className="ml-4">
            <span className="text-emerald-400">data-tool</span>=
            <span className="text-amber-300">"gmb-audit"</span>
            <span className="text-fuchsia-400">{'></script>'}</span>
          </div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-5 flex items-center gap-3"
      >
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 font-medium">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Live on your site
        </span>
        <span className="text-xs text-white/40">in &lt; 60 seconds</span>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────
export default function LeadGenToolsLanding() {
  // signup_page_viewed: visitor landed on the agency self-serve funnel. This
  // is a page mount, NOT an intent to sign up, and it used to fire as
  // signup_started, which made the funnel look like a 3% disaster by counting
  // every landing as an attempt. The click itself is tracked separately from
  // AuthProvider.
  useTrackOnce('signup_page_viewed', {
    page_slug: '/lead-gen-tools',
    source:
      (typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('source')
        : null) ?? 'lead_gen_landing',
  });

  const scrollToDemo = () => {
    document.getElementById('live-demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToCTA = () => {
    document.getElementById('final-cta')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // When the visitor arrives via /lead-gen-tools#faq (or any other
  // section anchor) from another page in the SPA, react-router's
  // <Link> doesn't auto-scroll to the hash. Scroll to it manually
  // once the DOM has had a chance to mount the targeted section.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.slice(1);
    // Two RAFs: first lets React commit the page; second lets layout
    // settle so getBoundingClientRect is accurate.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  // Embed-inquiry modal lifted to the page root so every CTA can open
  // it from anywhere in the tree via the openEmbedInquiry() helper /
  // 'open-embed-inquiry' CustomEvent.
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquirySource, setInquirySource] = useState('hero');
  const [inquiryVariant, setInquiryVariant] = useState<'embed' | 'talk'>('embed');
  useEffect(() => {
    const onOpen = (e: Event) => {
      const ce = e as CustomEvent<{ source?: string; variant?: 'embed' | 'talk' }>;
      setInquirySource(ce.detail?.source || 'unknown');
      setInquiryVariant(ce.detail?.variant || 'embed');
      setInquiryOpen(true);
    };
    window.addEventListener('open-embed-inquiry', onOpen);
    return () => window.removeEventListener('open-embed-inquiry', onOpen);
  }, []);

  // useAgencySelfServeListener used to be mounted here for the "Get my free
  // embed" CTAs. Those have all navigated to /pricing since 69798070 (June
  // 2026), so the listener had nothing to listen to on this page. It is now
  // mounted by ToolFunnelHeader, which this page renders, for the header's
  // own "Start free trial" button. Mounting it here as well would run the
  // sign-in and bootstrap twice per click.

  // Idle-prefetch the below-the-fold chunk so it is warm before the
  // visitor scrolls (the <Suspense> boundary also starts it on mount;
  // this just nudges it during the first idle window). No-op on SSR.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefetch = () => {
      void import('./LeadGenToolsLanding.belowFold');
    };
    const ric = (window as typeof window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    }).requestIdleCallback;
    if (ric) {
      const id = ric(prefetch);
      return () => (window as typeof window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(prefetch, 200);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="bg-gray-950 text-white antialiased">
      <Helmet>
        <title>White-Label Lead Gen Tools for Marketing Agencies | Fly</title>
        <meta
          name="description"
          content="Embed branded lead-capture audits on your agency site and turn your own website into a salesperson that qualifies visitors while you sleep."
        />
        <meta
          name="keywords"
          content="white label audit tools, agency lead generation, Google Business Profile audit, local rank checker, website SEO audit, AI search visibility, white label reports, marketing agency tools"
        />
        {/* Self-referencing canonical: the SSR server strips the static shell
            canonical for '/', so the homepage must re-add its own. */}
        <link rel="canonical" href="https://www.fly-social.com/" />
        {/* Open Graph / Twitter. The SSR server strips the static og and
            twitter tags for '/', so the full set (incl. image) must be
            re-emitted here or the homepage ships with no social card. */}
        <meta property="og:title" content="White-Label Lead Gen Tools for Marketing Agencies | Fly" />
        <meta
          property="og:description"
          content="Thirteen white-label audit and lead-gen tools for marketing agencies. Run branded audits on Google Business Profile, local rank, website SEO, AI search visibility and more. Client-ready reports in seconds, all under your brand."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fly-social.com/" />
        <meta property="og:site_name" content="Fly" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Fly white-label audit and lead-gen dashboard for agencies" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fly — White-Label Audit & Lead-Gen Tools for Agencies" />
        <meta
          name="twitter:description"
          content="Thirteen white-label audit and lead-gen tools for marketing agencies. Client-ready branded reports in seconds, all under your brand."
        />
        <meta name="twitter:image" content="https://www.fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <meta name="twitter:image:alt" content="Fly white-label audit and lead-gen dashboard for agencies" />
        {/* SoftwareApplication structured data (Change 1). This react-helmet
            block is the rendered source of truth and overrides the old static
            SoftwareApplication in index.html for JS-rendering crawlers. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Fly — White-Label Audit & Lead-Gen Tools for Agencies",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "Thirteen white-label audit and lead-gen tools for marketing agencies. Run branded audits on Google Business Profile, local rank, website SEO, AI search visibility and more — client-ready reports in seconds, all under your brand.",
          })}
        </script>
      </Helmet>

      {/* Shared stripped funnel header (logo only, no SiteNavBar) — keeps a
          focused funnel with no cross-navigation back into the marketing
          site. Reused on the tool pages so every funnel page is identical. */}
      {/* The header owns the pill nav now. This page mounts EmbedInquiryModal,
          so Contact Us opens the modal here rather than linking to /contact. */}
      <ToolFunnelHeader contactMode="modal" agentTab />

      <main>
        <Hero scrollToDemo={scrollToDemo} />
        <TrustStrip />
        {/* who joined Fly lately, the sign-up's list as a fast carousel beside the agencies' logos (Anand, 02 Nov 26) */}
        <JoinedCarousel tone="dark" />
        <TwoWays />
        <ValueTriplet />
        <ToolsSuite />
        <BeforeAfter />
        {/* "How it works" sits BEFORE Live Demo so the visitor learns
            the model in 3 steps, then immediately tries it. ROI lands
            after the demo as the dollars-and-cents follow-up. */}
        <HowItWorks />
        {/* Everything from the Live Demo down (ROI calculator, testimonials,
            numbers, pricing/cross-sell band, FAQ, blog, final CTA) is
            below the fold and ships in a separate React.lazy chunk so the
            hero + first sections paint from a lighter initial bundle. The
            chunk starts loading on mount, so #live-demo and the rest
            appear within a moment; the Suspense fallback is a plain
            height-reserving div (no spinner) to avoid layout shift. */}
        <Suspense fallback={<div className="min-h-screen" aria-hidden />}>
          <LeadGenToolsBelowFold />
        </Suspense>
      </main>

      <Footer compact />

      {/* Logo marquee carries the social proof now; the toast that used to
          sit here announced invented lead captures. */}
      {/* StickyCTABar removed — was conflicting visually with the
          branded "Ask Fly" chat banner in the bottom-right corner. */}

      {/* Single modal instance for every "Get my embed code" / "Talk to
          us" CTA on the page. Variant flips the title/CTA copy so the
          Pricing "Talk to us" button reads naturally instead of being
          mis-labeled as an embed request. */}
      <EmbedInquiryModal
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        source={inquirySource}
        title={inquiryVariant === 'talk' ? 'Talk to our team' : 'Get your embed code'}
        subtitle={
          inquiryVariant === 'talk'
            ? "Tell us about your agency and we'll set up a quick call this week."
            : "Tell us about your agency and we'll send your white-labeled embed within 24 hours."
        }
        ctaLabel={inquiryVariant === 'talk' ? 'Request a call' : 'Get my embed code'}
      />
    </div>
  );
}
